// BattleScene - Main game battle scene (Hearthstone-inspired layout)
import Phaser from 'phaser';
import { CardSprite } from '../objects/CardSprite';
import { CombatAnimationManager } from '../managers/CombatAnimationManager';
import { useGameStore } from '../../store/gameStore';
import { useUIStore } from '../../store/uiStore';
import { useUISettingsStore } from '../../store/uiSettingsStore';
import type { UISettings } from '../../store/uiSettingsStore';
import { SimpleAI, executeAIAction, type AIAction } from '../../services/SimpleAI';
import { HAND_SIZE_LIMIT } from '../../core/models/Player';
import { AudioManager } from '../../services/AudioManager';
import { sendGameCommand } from '../../services/GameEventHandler';
import { localToServerPosition } from '../../services/OnlineStateMapper';
import { getGlobalTimer, formatTime, getTimerLevel } from '../../core/systems/TimerManager';
import type { GameState, Card, CardDefinition, BattlePosition, TurnPhase, CombatResult } from '../../types';
import type { PendingChoice, ChoiceResult } from '../../core/effects';
import { getLogger } from '../../utils/logger';
import { preloadDeckImages } from '../../services/CardImageLoader';

const log = getLogger('BattleScene');

interface SlotUI {
  background: Phaser.GameObjects.Rectangle;
  position: BattlePosition;
  cardSprite: CardSprite | null;
}

export class BattleScene extends Phaser.Scene {
  // Layout constants (Hearthstone-style for 1920x1080) - SPACIOUS
  private static readonly SLOT_WIDTH = 130;
  private static readonly SLOT_HEIGHT = 140;
  private static readonly SLOT_GAP = 15;  // Horizontal spacing between slots
  private static readonly COLUMNS = 5;

  // Hand card layout constants reserved for future arc-style layout
  // HAND_CARD_WIDTH = 140, HAND_HOVER_SCALE = 2.0
  // HAND_ARC_RADIUS = 1200, HAND_MAX_ROTATION = 20

  // Mascot image path for Forest zone
  private static readonly FOREST_MASCOT_PATH = 'cards/03特殊旅人/SPT0042帶小弟的小旅人.jpg';

  // UI elements
  private slots: Map<string, SlotUI> = new Map();
  private player1HandCards: CardSprite[] = [];
  private player2HandCards: CardSprite[] = [];
  private phaseText!: Phaser.GameObjects.Text;
  private turnText!: Phaser.GameObjects.Text;
  private player1Info!: Phaser.GameObjects.Text;
  private player2Info!: Phaser.GameObjects.Text;
  // Action buttons removed - using endTurnButton instead
  // Reposition button (tactics phase only)
  private repositionButton!: Phaser.GameObjects.Container;
  private messageText!: Phaser.GameObjects.Text;

  // Zone slots (deck, graveyard, forest)
  private p1DeckSlot!: Phaser.GameObjects.Container;
  private p1GraveyardSlot!: Phaser.GameObjects.Container;
  private p1ForestSlot!: Phaser.GameObjects.Container;
  private p2DeckSlot!: Phaser.GameObjects.Container;
  private p2GraveyardSlot!: Phaser.GameObjects.Container;
  private p2ForestSlot!: Phaser.GameObjects.Container;

  // Card preview
  private previewContainer: Phaser.GameObjects.Container | null = null;
  private previewHideTimer: ReturnType<typeof setTimeout> | null = null;

  // Zone browser overlay
  private zoneBrowserContainer: Phaser.GameObjects.Container | null = null;

  // Hero portraits
  private p1HeroPortrait!: Phaser.GameObjects.Container;
  private p2HeroPortrait!: Phaser.GameObjects.Container;

  // End turn button
  private endTurnButton!: Phaser.GameObjects.Container;
  // Surrender button (online mode only)
  private surrenderButton!: Phaser.GameObjects.Container;

  // Game timer display
  private timerText!: Phaser.GameObjects.Text;
  private timerUnsubscribe: (() => void) | null = null;

  // Hand count display
  private p1HandCountText!: Phaser.GameObjects.Text;
  private p2HandCountText!: Phaser.GameObjects.Text;

  // 動作提示文字
  private actionHintText!: Phaser.GameObjects.Text;

  // Phase transition animation
  private lastDisplayedPhase: TurnPhase | null = null;
  private phaseTransitionContainer: Phaser.GameObjects.Container | null = null;

  // State
  private selectedCardId: string | null = null;
  private gameState: GameState | null = null;
  private cardDefinitions: Map<string, CardDefinition> = new Map();
  private unsubscribe: (() => void) | null = null;

  // AI
  private ai: SimpleAI | null = null;
  private isAITurnActive = false;
  private aiActionTimeout: ReturnType<typeof setTimeout> | null = null;

  // Auto-advance for draw/defense phases
  private isAutoAdvancing = false;
  private autoAdvanceTimeout: ReturnType<typeof setTimeout> | null = null;

  // Combat animations
  private combatAnimationManager: CombatAnimationManager | null = null;

  // Attack arrow
  private attackArrow: Phaser.GameObjects.Graphics | null = null;

  // Choice system
  private pendingChoice: PendingChoice | null = null;
  private validTargetIds: Set<string> = new Set();
  private choiceUnsubscribe: (() => void) | null = null;

  // UI Settings
  private settingsGearButton: Phaser.GameObjects.Text | null = null;
  private settingsPanelContainer: Phaser.GameObjects.Container | null = null;
  private settingsPanelVisible = false;
  private lastUISettingsVersion = -1;
  private settingsUnsubscribe: (() => void) | null = null;

  constructor() {
    super({ key: 'BattleScene' });
  }

  // ============================================================
  // 測試 API - 給自動化測試和控制台使用
  // ============================================================

  /**
   * 測試 API
   * 使用方式: window.__GAME__.scene.getScene('BattleScene').testAPI.xxx()
   */
  public testAPI = {
    /** 取得目前遊戲狀態 */
    getState: () => this.gameState,

    /** 取得手牌 */
    getHand: () => {
      if (!this.gameState) return [];
      const player = this.gameState.player1;
      return player.hand.map((card, idx) => ({
        index: idx,
        id: card.id,
        definitionId: card.definitionId,
        name: this.cardDefinitions.get(card.definitionId)?.nameZh ?? card.definitionId,
      }));
    },

    /** 選擇手牌 */
    selectHandCard: (index: number) => {
      if (!this.gameState) return { success: false, error: 'No game state' };
      const hand = this.gameState.player1.hand;
      if (index < 0 || index >= hand.length) {
        return { success: false, error: `Invalid index: ${String(index)}` };
      }
      this.selectedCardId = hand[index].id;
      this.updateHandSelection();
      return { success: true, selectedId: this.selectedCardId };
    },

    /** 放置卡牌到指定位置 */
    placeCard: (row: 'front' | 'back', column: number) => {
      if (!this.selectedCardId) {
        return { success: false, error: 'No card selected' };
      }
      const store = useGameStore.getState();
      const position = {
        side: 'player1' as const,
        row,
        column: column as 1 | 2 | 3 | 4 | 5,
      };
      const result = store.turnExecuteSummon(this.selectedCardId, position);
      if (result.success) {
        this.selectedCardId = null;
        this.clearHighlights();
      }
      return result;
    },

    /** 結束目前階段 */
    endPhase: () => {
      const store = useGameStore.getState();
      return store.turnEndPhase();
    },

    /** 宣告攻擊 */
    declareAttack: (cardIds: string[]) => {
      const store = useGameStore.getState();
      return store.turnExecuteAttack(cardIds);
    },

    /** 取得戰場上的單位 */
    getBattlefield: () => {
      const state = this.gameState;
      if (!state) return null;
      const result: Record<string, unknown>[] = [];
      
      const processSide = (side: 'player1' | 'player2') => {
        const sideData = side === 'player1' 
          ? state.battlefield.player1Side 
          : state.battlefield.player2Side;
        
        for (const row of ['front', 'back'] as const) {
          const line = row === 'front' ? sideData.frontLine : sideData.backLine;
          line.forEach((slot, idx) => {
            if (slot.unit) {
              result.push({
                side,
                row,
                column: idx + 1,
                id: slot.unit.id,
                definitionId: slot.unit.definitionId,
                name: this.cardDefinitions.get(slot.unit.definitionId)?.nameZh ?? slot.unit.definitionId,
                orientation: slot.unit.orientation,
              });
            }
          });
        }
      };
      
      processSide('player1');
      processSide('player2');
      return result;
    },

    /** 取得目前階段 */
    getCurrentPhase: () => this.gameState?.currentPhase ?? null,

    /** 取得目前回合 */
    getCurrentTurn: () => this.gameState?.turnNumber ?? 0,

    /** 顯示訊息 */
    showMessage: (msg: string) => { this.showMessage(msg); },
  };

  preload(): void {
    // Load background assets
    this.load.image('starry-bg', 'assets/backgrounds/starry-bg.jpg');
    this.load.image('logo', 'assets/ui/logo.png');
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Create background layers
    this.createBackground(width, height);

    // Create hero portraits (Hearthstone-style)
    this.createHeroPortraits(width, height);

    // Create battlefield slots
    this.createBattlefieldSlots(width, height);

    // Create zone slots (deck, graveyard, forest)
    this.createZoneSlots(width, height);

    // Create end turn button (prominent, right side)
    this.createEndTurnButton(width, height);

    // Create surrender button (online mode only)
    this.createSurrenderButton(width, height);

    // Create reposition button (tactics phase only)
    this.createRepositionButton(width, height);

    // Create UI elements
    this.createPhaseIndicator(width);
    this.createGameTimer(width);
    this.createPlayerInfo();
    this.createMessageArea(width, height);

    // Create UI settings gear button
    this.createSettingsGearButton(width);

    // Subscribe to store changes
    this.subscribeToStore();

    // Subscribe to UI settings changes
    this.subscribeToUISettings();

    // 只在本地模式下初始化 AI
    const store = useGameStore.getState();
    if (store.gameState && store.gameMode === 'local') {
      this.ai = new SimpleAI(store.gameState.player2.id);
    }
    // 連線模式下 AI 不啟動，對手操作由伺服器事件驅動

    // Initialize combat animation manager
    this.combatAnimationManager = new CombatAnimationManager(this);

    // === Drag and Drop system ===
    this.setupDragAndDrop();

    // Preload card images then render (fixes cards showing fallback colors)
    void this.preloadAndRender();
  }

  /**
   * 確保卡牌圖片載入完成後才渲染手牌和戰場。
   * 解決 MainScene preload 的 texture 可能在場景切換時未完全就緒的問題。
   */
  private async preloadAndRender(): Promise<void> {
    const store = useGameStore.getState();
    if (!store.gameState) {
      this.refreshGameState();
      return;
    }

    // Collect all card definitions currently in use
    const allCardDefs: CardDefinition[] = [];
    const seen = new Set<string>();

    const addCards = (cards: Card[]) => {
      for (const card of cards) {
        if (seen.has(card.definitionId)) continue;
        seen.add(card.definitionId);
        const def = store.cardDefinitions.get(card.definitionId);
        if (def?.imagePath) allCardDefs.push(def);
      }
    };

    const gs = store.gameState;
    addCards(gs.player1.hand);
    addCards(gs.player2.hand);
    addCards(gs.player1.deck);
    addCards(gs.player2.deck);
    // Battlefield cards
    const allSlots = [
      ...gs.battlefield.player1Side.frontLine,
      ...gs.battlefield.player1Side.backLine,
      ...gs.battlefield.player2Side.frontLine,
      ...gs.battlefield.player2Side.backLine,
    ];
    for (const slot of allSlots) {
      if (slot.unit) addCards([slot.unit]);
    }

    if (allCardDefs.length > 0) {
      const log = getLogger('BattleScene');
      log.debug(`Preloading ${String(allCardDefs.length)} card images in BattleScene...`);
      const result = await preloadDeckImages(this, allCardDefs);
      log.debug(`Preload done: ${String(result.loaded)} loaded, ${String(result.failed)} failed`);
    }

    // Now render with all textures available
    this.refreshGameState();
  }

  private createBackground(width: number, height: number): void {
    // Add starry background image if available
    if (this.textures.exists('starry-bg')) {
      const bg = this.add.image(width / 2, height / 2, 'starry-bg');
      const scaleX = width / bg.width;
      const scaleY = height / bg.height;
      const scale = Math.max(scaleX, scaleY);
      bg.setScale(scale);
      bg.setAlpha(0.85);
    } else {
      // Fallback: cosmic gradient (dark blue → deep purple)
      const graphics = this.add.graphics();
      graphics.fillGradientStyle(0x060618, 0x060618, 0x1a0a2e, 0x1a0a2e);
      graphics.fillRect(0, 0, width, height);
    }

    // Twinkling star particles
    this.createStarParticles(width, height);

    // Battlefield area overlay
    const graphics = this.add.graphics();
    const battleAreaY = height * 0.06;
    const battleAreaHeight = height * 0.60;
    graphics.fillStyle(0x000000, 0.3);
    graphics.fillRoundedRect(180, battleAreaY, width - 360, battleAreaHeight, 16);

    // Center divider with glow effect
    const centerY = height / 2 - 60;

    // Outer glow (wider, dimmer)
    graphics.lineStyle(8, 0xffd700, 0.15);
    graphics.lineBetween(220, centerY, width - 220, centerY);
    // Core line (bright golden)
    graphics.lineStyle(2, 0xffd700, 0.9);
    graphics.lineBetween(220, centerY, width - 220, centerY);

    // Column number markers at center line (1-5)
    const slotTotalWidth = BattleScene.COLUMNS * (BattleScene.SLOT_WIDTH + BattleScene.SLOT_GAP);
    const startX = width / 2 - slotTotalWidth / 2 + BattleScene.SLOT_WIDTH / 2;
    for (let col = 1; col <= 5; col++) {
      const x = startX + (col - 1) * (BattleScene.SLOT_WIDTH + BattleScene.SLOT_GAP);
      // Star burst marker
      const star = this.add.text(x, centerY, '✦', {
        fontSize: '22px',
        color: '#ffd700',
        fontStyle: 'bold',
      }).setOrigin(0.5).setAlpha(0.7);
      // Column number
      this.add.text(x, centerY + 16, String(col), {
        fontSize: '11px',
        color: '#997a00',
        fontStyle: 'bold',
      }).setOrigin(0.5).setAlpha(0.6);

      // Subtle pulsing on star markers
      this.tweens.add({
        targets: star,
        alpha: { from: 0.5, to: 0.9 },
        duration: 1500 + col * 200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // 中線兩端裝飾（菱形端點）
    const lineStartX = 220;
    const lineEndX = width - 220;
    for (const ex of [lineStartX, lineEndX]) {
      const diamond = this.add.text(ex, centerY, '◆', {
        fontSize: '16px',
        color: '#ffd700',
        fontStyle: 'bold',
      }).setOrigin(0.5).setAlpha(0.7);
      this.tweens.add({
        targets: diamond,
        alpha: { from: 0.4, to: 0.9 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Decorative border
    graphics.lineStyle(2, 0x4a6a9a, 0.6);
    graphics.strokeRoundedRect(180, battleAreaY, width - 360, battleAreaHeight, 16);
  }

  /**
   * Create twinkling star particle effect for the background
   */
  private createStarParticles(width: number, height: number): void {
    const starCount = 40;
    for (let i = 0; i < starCount; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = 1 + Math.random() * 2;
      const alpha = 0.2 + Math.random() * 0.5;

      const star = this.add.rectangle(x, y, size, size, 0xffffff, alpha);
      star.setDepth(0);

      this.tweens.add({
        targets: star,
        alpha: { from: alpha * 0.3, to: alpha },
        duration: 1500 + Math.random() * 3000,
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 2000,
        ease: 'Sine.easeInOut',
      });
    }
  }

  // Score text references for dynamic update
  private p1ScoreText!: Phaser.GameObjects.Text;
  private p2ScoreText!: Phaser.GameObjects.Text;
  private p1FactionText!: Phaser.GameObjects.Text;
  private p2FactionText!: Phaser.GameObjects.Text;

  private createHeroPortraits(width: number, height: number): void {
    // === Player 1 (bottom right) ===
    this.p1HeroPortrait = this.add.container(width - 100, height - 280);

    // Circular frame (outer glow ring)
    const p1GlowRing = this.add.circle(0, 0, 48, 0xffd700, 0.1);
    // Inner portrait frame
    const p1FrameOuter = this.add.circle(0, 0, 44, 0x3a2a1a);
    p1FrameOuter.setStrokeStyle(3, 0xffd700, 0.8);
    const p1Icon = this.add.text(0, 0, '👤', { fontSize: '36px' }).setOrigin(0.5);

    // Player name label
    const p1Label = this.add.text(0, 58, '玩家', {
      fontSize: '14px', color: '#ffd700', fontStyle: 'bold',
    }).setOrigin(0.5);

    // Faction label
    this.p1FactionText = this.add.text(0, 74, '', {
      fontSize: '11px', color: '#aaaaaa',
    }).setOrigin(0.5);

    // Score display (prominent)
    const p1ScoreBg = this.add.rectangle(0, -60, 52, 28, 0x1a3a6a, 0.9);
    p1ScoreBg.setStrokeStyle(1.5, 0x4488ff, 0.8);
    const p1StarIcon = this.add.text(-18, -60, '⭐', { fontSize: '12px' }).setOrigin(0.5);
    this.p1ScoreText = this.add.text(6, -60, '0', {
      fontSize: '16px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);

    // Hand count
    this.p1HandCountText = this.add.text(55, 0, `🃏 0/${String(HAND_SIZE_LIMIT)}`, {
      fontSize: '12px', color: '#aaaaaa', fontStyle: 'bold',
    }).setOrigin(0, 0.5);

    this.p1HeroPortrait.add([p1GlowRing, p1FrameOuter, p1Icon, p1Label, this.p1FactionText,
      p1ScoreBg, p1StarIcon, this.p1ScoreText, this.p1HandCountText]);

    // === Player 2 (top right) ===
    this.p2HeroPortrait = this.add.container(width - 100, 150);

    const p2GlowRing = this.add.circle(0, 0, 48, 0xff4444, 0.08);
    const p2FrameOuter = this.add.circle(0, 0, 44, 0x3a1a1a);
    p2FrameOuter.setStrokeStyle(3, 0xcc4444, 0.8);
    const p2Icon = this.add.text(0, 0, '👤', { fontSize: '36px' }).setOrigin(0.5);

    const p2Label = this.add.text(0, 58, '對手', {
      fontSize: '14px', color: '#ff8888', fontStyle: 'bold',
    }).setOrigin(0.5);

    this.p2FactionText = this.add.text(0, 74, '', {
      fontSize: '11px', color: '#aaaaaa',
    }).setOrigin(0.5);

    // Score display
    const p2ScoreBg = this.add.rectangle(0, -60, 52, 28, 0x3a1a1a, 0.9);
    p2ScoreBg.setStrokeStyle(1.5, 0xcc4444, 0.8);
    const p2StarIcon = this.add.text(-18, -60, '⭐', { fontSize: '12px' }).setOrigin(0.5);
    this.p2ScoreText = this.add.text(6, -60, '0', {
      fontSize: '16px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);

    // Hand count
    this.p2HandCountText = this.add.text(55, 0, `🃏 0/${String(HAND_SIZE_LIMIT)}`, {
      fontSize: '12px', color: '#aaaaaa', fontStyle: 'bold',
    }).setOrigin(0, 0.5);

    this.p2HeroPortrait.add([p2GlowRing, p2FrameOuter, p2Icon, p2Label, this.p2FactionText,
      p2ScoreBg, p2StarIcon, this.p2ScoreText, this.p2HandCountText]);
  }

  private endTurnPulseTween: Phaser.Tweens.Tween | null = null;

  private createEndTurnButton(width: number, height: number): void {
    this.endTurnButton = this.add.container(width - 80, height / 2 - 50);

    // Outer glow frame (pulsing)
    const glowOuter = this.add.rectangle(0, 0, 118, 78, 0xffd700, 0);
    this.endTurnButton.add(glowOuter);

    // Decorative double border
    const borderOuter = this.add.rectangle(0, 0, 112, 72, 0x000000, 0);
    borderOuter.setStrokeStyle(3, 0x8b6914, 0.6);
    this.endTurnButton.add(borderOuter);

    // Button background
    const btnBg = this.add.rectangle(0, 0, 104, 64, 0x3a2a10);
    btnBg.setStrokeStyle(2, 0xffd700, 0.9);
    btnBg.setInteractive({ useHandCursor: true });
    this.endTurnButton.add(btnBg);

    // Icon + text
    const btnText = this.add.text(0, 0, '⏩ 結束回合', {
      fontSize: '15px',
      color: '#ffd700',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.endTurnButton.add(btnText);

    this.endTurnButton.setData('glow', glowOuter);
    this.endTurnButton.setData('bg', btnBg);
    this.endTurnButton.setData('text', btnText);

    // Pulsing glow to remind player
    this.endTurnPulseTween = this.tweens.add({
      targets: glowOuter,
      alpha: { from: 0, to: 0.25 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    btnBg.on('pointerover', () => {
      btnBg.setFillStyle(0x5a4a20);
      glowOuter.setAlpha(0.35);
      btnText.setColor('#ffee88');
    });
    btnBg.on('pointerout', () => {
      btnBg.setFillStyle(0x3a2a10);
      btnText.setColor('#ffd700');
    });
    btnBg.on('pointerdown', () => {
      // Click feedback: brief scale
      this.tweens.add({
        targets: this.endTurnButton,
        scaleX: 0.92,
        scaleY: 0.92,
        duration: 80,
        yoyo: true,
        ease: 'Power2',
      });
      AudioManager.play('buttonClick');
      this.onActionButton('endPhase');
    });
  }

  private createSurrenderButton(width: number, height: number): void {
    // Surrender button below the end turn button, only visible in online mode
    this.surrenderButton = this.add.container(width - 80, height / 2 + 20);

    const btnBg = this.add.rectangle(0, 0, 80, 36, 0x3a1a1a);
    btnBg.setStrokeStyle(2, 0x884444);
    btnBg.setInteractive({ useHandCursor: true });

    const btnText = this.add.text(0, 0, '投降', {
      fontSize: '14px',
      color: '#ff6666',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.surrenderButton.add([btnBg, btnText]);

    btnBg.on('pointerover', () => {
      btnBg.setFillStyle(0x5a2a2a);
    });
    btnBg.on('pointerout', () => {
      btnBg.setFillStyle(0x3a1a1a);
    });
    btnBg.on('pointerdown', () => {
      AudioManager.play('buttonClick');
      this.onActionButton('surrender');
    });

    // Initially hidden; shown only in online mode
    const store = useGameStore.getState();
    this.surrenderButton.setVisible(store.gameMode === 'online');
  }

  private createRepositionButton(width: number, height: number): void {
    // Reposition button below end turn, shown only during tactics phase
    this.repositionButton = this.add.container(width - 80, height / 2 + 20);

    const btnBg = this.add.rectangle(0, 0, 90, 36, 0x1a3a2a);
    btnBg.setStrokeStyle(2, 0x44aa66);
    btnBg.setInteractive({ useHandCursor: true });

    const btnText = this.add.text(0, 0, '🔄 調整位置', {
      fontSize: '12px',
      color: '#66cc88',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.repositionButton.add([btnBg, btnText]);
    this.repositionButton.setData('bg', btnBg);
    this.repositionButton.setData('text', btnText);

    btnBg.on('pointerover', () => {
      btnBg.setFillStyle(0x2a5a3a);
    });
    btnBg.on('pointerout', () => {
      btnBg.setFillStyle(0x1a3a2a);
    });
    btnBg.on('pointerdown', () => {
      AudioManager.play('buttonClick');
      this.toggleRepositionMode();
    });

    // Initially hidden
    this.repositionButton.setVisible(false);
  }

  /**
   * Toggle reposition mode: select a unit on your field, then click another slot to swap/move.
   */
  private toggleRepositionMode(): void {
    const uiStore = useUIStore.getState();

    if (uiStore.unitMoveSource) {
      // Cancel reposition mode
      uiStore.setUnitMoveSource(null);
      this.clearHighlights();
      this.showMessage('');
      (this.repositionButton.getData('text') as Phaser.GameObjects.Text).setText('🔄 調整位置');
      const btnBg = this.repositionButton.getData('bg') as Phaser.GameObjects.Rectangle;
      btnBg.setFillStyle(0x1a3a2a);
      btnBg.setStrokeStyle(2, 0x44aa66);
    } else {
      // Enter reposition mode - prompt to select a unit
      this.showMessage('點擊場上自己的單位選擇要移動的單位');
      this.highlightPlayerUnitsForReposition();
      (this.repositionButton.getData('text') as Phaser.GameObjects.Text).setText('❌ 取消移動');
      const btnBg = this.repositionButton.getData('bg') as Phaser.GameObjects.Rectangle;
      btnBg.setFillStyle(0x3a2a1a);
      btnBg.setStrokeStyle(2, 0xaa6644);
      this.repositionModeActive = true;
    }
  }

  private repositionModeActive = false;

  /**
   * Highlight all player units for reposition selection.
   */
  private highlightPlayerUnitsForReposition(): void {
    this.slots.forEach((slot) => {
      if (slot.position.side === 'player1' && slot.cardSprite) {
        slot.cardSprite.highlight(0x66cc88); // green highlight
      }
    });
  }

  /**
   * Update end turn button text based on tactics phase state.
   * Shows "⚔️ 宣告攻擊" when attackers are selected, otherwise "⏩ 結束階段".
   */
  private updateEndTurnButtonForTactics(): void {
    if (!this.gameState) return;

    const btnText = this.endTurnButton.getData('text') as Phaser.GameObjects.Text | undefined;
    if (!btnText) return;

    if (this.gameState.currentPhase === 'tactics') {
      const uiStore = useUIStore.getState();
      if (uiStore.selectedAttackers.length > 0) {
        btnText.setText(`⚔️ 宣告攻擊 (${String(uiStore.selectedAttackers.length)})`);
        btnText.setColor('#ff6644');
      } else {
        btnText.setText('⏩ 結束階段');
        btnText.setColor('#ffd700');
      }
    } else {
      // Reset to default for non-tactics phases
      btnText.setText('⏩ 結束回合');
      btnText.setColor('#ffd700');
    }
  }

  private createBattlefieldSlots(width: number, height: number): void {
    const centerX = width / 2;
    const slotTotalWidth = BattleScene.COLUMNS * (BattleScene.SLOT_WIDTH + BattleScene.SLOT_GAP);
    const startX = centerX - slotTotalWidth / 2 + BattleScene.SLOT_WIDTH / 2;

    // Battlefield centered
    const centerY = height / 2 - 60;

    // Row spacing - slot height + gap to prevent overlap
    const rowGap = BattleScene.SLOT_HEIGHT + 15;  // 155px between row centers

    // Player 2 (opponent) - above center line
    const p2FrontY = centerY - rowGap / 2;         // Front row just above center
    const p2BackY = p2FrontY - rowGap;             // Back row above front

    // Player 1 (self) - below center line
    const p1FrontY = centerY + rowGap / 2;         // Front row just below center
    const p1BackY = p1FrontY + rowGap;             // Back row below front

    // Create slots for each position
    for (let col = 1; col <= 5; col++) {
      const x = startX + (col - 1) * (BattleScene.SLOT_WIDTH + BattleScene.SLOT_GAP);

      // Player 2 back row
      this.createSlot(x, p2BackY, { side: 'player2', row: 'back', column: col as 1 | 2 | 3 | 4 | 5 });
      // Player 2 front row
      this.createSlot(x, p2FrontY, { side: 'player2', row: 'front', column: col as 1 | 2 | 3 | 4 | 5 });
      // Player 1 front row
      this.createSlot(x, p1FrontY, { side: 'player1', row: 'front', column: col as 1 | 2 | 3 | 4 | 5 });
      // Player 1 back row
      this.createSlot(x, p1BackY, { side: 'player1', row: 'back', column: col as 1 | 2 | 3 | 4 | 5 });
    }

    // Row labels with golden color
    const labelStyle = { fontSize: '13px', color: '#8b6914', fontStyle: 'bold' };
    this.add.text(startX - 65, p2BackY, '後排', labelStyle as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5, 0.5);
    this.add.text(startX - 65, p2FrontY, '前排', labelStyle as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5, 0.5);
    this.add.text(startX - 65, p1FrontY, '前排', labelStyle as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5, 0.5);
    this.add.text(startX - 65, p1BackY, '後排', labelStyle as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5, 0.5);
  }

  private createZoneSlots(_width: number, height: number): void {
    const slotWidth = 70;
    const slotHeight = 90;
    const zoneGap = 6;

    // Forest zone is larger to show mascot
    const forestWidth = 85;
    const forestHeight = 110;

    // Left side zones - positioned to not overlap with battlefield
    const leftX = 80;

    // Player 2 zones (top-left area)
    const p2ZoneY = 90;
    this.p2DeckSlot = this.createZoneSlot(leftX, p2ZoneY, slotWidth, slotHeight, '牌庫', 0x2a3a4a);
    this.p2GraveyardSlot = this.createZoneSlot(leftX + slotWidth + zoneGap, p2ZoneY, slotWidth, slotHeight, '墓地', 0x3a2a3a);
    this.p2ForestSlot = this.createForestSlot(leftX, p2ZoneY + slotHeight + zoneGap, forestWidth, forestHeight);

    // Player 1 zones (bottom-left area) - above the hand cards
    const p1ZoneY = height - 320;
    this.p1DeckSlot = this.createZoneSlot(leftX, p1ZoneY, slotWidth, slotHeight, '牌庫', 0x2a3a4a);
    this.p1GraveyardSlot = this.createZoneSlot(leftX + slotWidth + zoneGap, p1ZoneY, slotWidth, slotHeight, '墓地', 0x3a2a3a);
    this.p1ForestSlot = this.createForestSlot(leftX, p1ZoneY + slotHeight + zoneGap, forestWidth, forestHeight);

    // 設定區域點擊事件
    this.setupZoneClickHandler(this.p1DeckSlot, () => { this.openZoneBrowser('牌庫', this.gameState?.player1.deck ?? [], true); });
    this.setupZoneClickHandler(this.p1GraveyardSlot, () => { this.openZoneBrowser('墓地', this.gameState?.player1.graveyard ?? []); });
    this.setupZoneClickHandler(this.p1ForestSlot, () => { this.openZoneBrowser('森林', this.gameState?.player1.forest ?? []); });
    this.setupZoneClickHandler(this.p2DeckSlot, () => { this.openZoneBrowser('對手牌庫', this.gameState?.player2.deck ?? [], true); });
    this.setupZoneClickHandler(this.p2GraveyardSlot, () => { this.openZoneBrowser('對手墓地', this.gameState?.player2.graveyard ?? []); });
    this.setupZoneClickHandler(this.p2ForestSlot, () => { this.openZoneBrowser('對手森林', this.gameState?.player2.forest ?? []); });
  }

  private createForestSlot(x: number, y: number, width: number, height: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Background with forest color
    const bg = this.add.rectangle(0, 0, width, height, 0x1a3a1a, 0.9);
    bg.setStrokeStyle(3, 0x4a8a4a);
    container.add(bg);

    // Try to load and display the mascot image
    const mascotKey = 'forest_mascot';
    if (this.textures.exists(mascotKey)) {
      const mascotImg = this.add.image(0, 0, mascotKey);
      mascotImg.setDisplaySize(width - 8, height - 30);
      mascotImg.setY(-8);
      container.add(mascotImg);
    } else {
      // Load the mascot image if not loaded
      this.load.image(mascotKey, `/assets/${BattleScene.FOREST_MASCOT_PATH}`);
      this.load.once('complete', () => {
        if (this.textures.exists(mascotKey)) {
          const mascotImg = this.add.image(0, -8, mascotKey);
          mascotImg.setDisplaySize(width - 8, height - 30);
          container.add(mascotImg);
          container.sendToBack(bg);
        }
      });
      this.load.start();
    }

    // Label at bottom
    const labelText = this.add.text(0, height / 2 - 12, '森林', {
      fontSize: '12px',
      color: '#88ff88',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(labelText);

    // Count text (small, at corner)
    const countText = this.add.text(width / 2 - 8, -height / 2 + 12, '∞', {
      fontSize: '16px',
      color: '#88ff88',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(countText);
    container.setData('countText', countText);

    return container;
  }

  private createZoneSlot(x: number, y: number, width: number, height: number, label: string, color: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Background
    const bg = this.add.rectangle(0, 0, width, height, color, 0.7);
    bg.setStrokeStyle(2, 0x6a6a8a);
    container.add(bg);

    // Label at top
    const labelText = this.add.text(0, -height / 2 + 14, label, {
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(labelText);

    // Count text at center
    const countText = this.add.text(0, 10, '0', {
      fontSize: '28px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(countText);
    container.setData('countText', countText);

    return container;
  }

  private createSlot(x: number, y: number, position: BattlePosition): void {
    const slotKey = this.getSlotKey(position);

    // Row-based color coding: front=warm gold/orange, back=cool blue/teal
    const isFront = position.row === 'front';
    const borderColor = isFront ? 0xcc8822 : 0x4a6a9a;
    const borderAlpha = 0.3;
    const fillColor = isFront ? 0x1a2a3a : 0x1a2a3a;

    const background = this.add.rectangle(
      x, y,
      BattleScene.SLOT_WIDTH,
      BattleScene.SLOT_HEIGHT,
      fillColor,
      0.15
    );
    background.setStrokeStyle(1, borderColor, borderAlpha);

    // Rounded corner visual (Phaser rectangles don't round, overlay a rounded stroke)
    const gfx = this.add.graphics();
    gfx.lineStyle(1, borderColor, borderAlpha);
    gfx.strokeRoundedRect(
      x - BattleScene.SLOT_WIDTH / 2,
      y - BattleScene.SLOT_HEIGHT / 2,
      BattleScene.SLOT_WIDTH,
      BattleScene.SLOT_HEIGHT,
      6
    );
    // Hide the rectangle's own stroke, use the rounded one
    background.setStrokeStyle(0);

    background.setInteractive({ useHandCursor: true });

    // Slot click handler
    background.on('pointerdown', () => { this.onSlotClick(position); });
    background.on('pointerover', () => {
      if (!this.slots.get(slotKey)?.cardSprite) {
        gfx.clear();
        gfx.lineStyle(2.5, isFront ? 0xffaa44 : 0x44ccee, 0.9);
        gfx.strokeRoundedRect(
          x - BattleScene.SLOT_WIDTH / 2,
          y - BattleScene.SLOT_HEIGHT / 2,
          BattleScene.SLOT_WIDTH,
          BattleScene.SLOT_HEIGHT,
          6
        );
        background.setFillStyle(isFront ? 0x3a3020 : 0x203040, 0.7);
      }
    });
    background.on('pointerout', () => {
      gfx.clear();
      gfx.lineStyle(1, borderColor, borderAlpha);
      gfx.strokeRoundedRect(
        x - BattleScene.SLOT_WIDTH / 2,
        y - BattleScene.SLOT_HEIGHT / 2,
        BattleScene.SLOT_WIDTH,
        BattleScene.SLOT_HEIGHT,
        6
      );
      background.setFillStyle(fillColor, 0.15);
    });

    // Store the graphics ref for later highlight updates
    background.setData('slotGfx', gfx);
    background.setData('borderColor', borderColor);
    background.setData('borderAlpha', borderAlpha);
    background.setData('fillColor', fillColor);
    background.setData('isFront', isFront);

    this.slots.set(slotKey, {
      background,
      position,
      cardSprite: null,
    });
  }

  private getSlotKey(position: BattlePosition): string {
    return `${position.side}_${position.row}_${String(position.column)}`;
  }

  private createPhaseIndicator(width: number): void {
    const centerX = width / 2;

    // Background banner with gradient-like layered effect
    const bannerOuter = this.add.rectangle(centerX, 35, 340, 56, 0x0d0a04, 0.95);
    bannerOuter.setStrokeStyle(2, 0x8b6914);
    this.add.rectangle(centerX, 35, 332, 48, 0x1a1208, 0.9);

    // Turn number (left side)
    this.turnText = this.add.text(centerX - 100, 35, '第 1 回合', {
      fontSize: '16px',
      color: '#ffd700',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Divider dot
    this.add.text(centerX - 20, 35, '|', {
      fontSize: '16px',
      color: '#8b6914',
    }).setOrigin(0.5);

    // Phase name (right side)
    this.phaseText = this.add.text(centerX + 60, 35, '🃏 抽牌階段', {
      fontSize: '16px',
      color: '#88ccff',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Active player indicator below banner
    this.add.text(centerX, 62, '', { fontSize: '1px' }); // placeholder
  }

  private createGameTimer(width: number): void {
    // Timer display at top-right corner
    this.timerText = this.add.text(width - 20, 35, '30:00', {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(1, 0.5);

    // Subscribe to timer ticks
    const timer = getGlobalTimer();
    this.timerUnsubscribe = timer.onTick((remaining) => {
      this.timerText.setText(formatTime(remaining));
      const level = getTimerLevel(remaining);
      switch (level) {
        case 'critical':
          this.timerText.setColor('#ff3333');
          break;
        case 'warning':
          this.timerText.setColor('#ffaa00');
          break;
        case 'expired':
          this.timerText.setColor('#ff0000');
          this.timerText.setText('TIME UP');
          break;
        default:
          this.timerText.setColor('#ffffff');
          break;
      }
    });

    // Initially show current time if timer is running
    const remaining = timer.getRemainingTime();
    if (remaining > 0) {
      this.timerText.setText(formatTime(remaining));
    }
  }

  private createPlayerInfo(): void {
    // Player info is now shown in hero portraits
    // These text objects are kept for compatibility but hidden
    this.player2Info = this.add.text(0, 0, '', { fontSize: '1px' });
    this.player2Info.setVisible(false);
    this.player1Info = this.add.text(0, 0, '', { fontSize: '1px' });
    this.player1Info.setVisible(false);
  }

  private createMessageArea(width: number, height: number): void {
    this.messageText = this.add.text(width / 2, height / 2, '', {
      fontSize: '20px',
      color: '#ffcc00',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 動作提示文字（顯示在手牌上方）
    this.actionHintText = this.add.text(width / 2, height - 210, '', {
      fontSize: '16px',
      color: '#88aacc',
      fontStyle: 'bold',
      backgroundColor: '#00000088',
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(90);
  }

  private subscribeToStore(): void {
    // Subscribe to game state changes
    this.unsubscribe = useGameStore.subscribe(
      (state) => ({ gameState: state.gameState, cardDefinitions: state.cardDefinitions }),
      (newState) => {
        this.gameState = newState.gameState;
        this.cardDefinitions = newState.cardDefinitions;
        this.refreshGameState();
      }
    );

    // Subscribe to pending choice changes
    this.choiceUnsubscribe = useGameStore.subscribe(
      (state) => state.pendingChoice,
      (newPendingChoice) => {
        this.pendingChoice = newPendingChoice;
        this.updateChoiceHighlights();
      }
    );
  }

  /**
   * 更新選擇系統的高亮顯示
   */
  private updateChoiceHighlights(): void {
    // 清除舊的高亮
    this.clearChoiceHighlights();

    if (!this.pendingChoice || this.pendingChoice.type !== 'target') {
      return;
    }

    // 取得有效目標
    const store = useGameStore.getState();
    const validTargets = store.getValidTargets();
    this.validTargetIds = new Set(validTargets.map(c => c.id));

    // 高亮有效目標
    this.slots.forEach((slot) => {
      if (slot.cardSprite && this.validTargetIds.has(slot.cardSprite.cardId)) {
        slot.cardSprite.highlight(0x00ffff); // 青色高亮
        const gfx = slot.background.getData('slotGfx') as Phaser.GameObjects.Graphics | undefined;
        if (gfx) {
          const x = slot.background.x;
          const y = slot.background.y;
          gfx.clear();
          gfx.lineStyle(3, 0x00ffff, 0.9);
          gfx.strokeRoundedRect(
            x - BattleScene.SLOT_WIDTH / 2,
            y - BattleScene.SLOT_HEIGHT / 2,
            BattleScene.SLOT_WIDTH,
            BattleScene.SLOT_HEIGHT,
            6
          );
        } else {
          slot.background.setStrokeStyle(3, 0x00ffff);
        }
      }
    });
  }

  /**
   * 清除選擇系統的高亮
   */
  private clearChoiceHighlights(): void {
    this.validTargetIds.clear();
    this.slots.forEach((slot) => {
      if (slot.cardSprite) {
        slot.cardSprite.clearHighlight();
      }
      // Reset slot border via graphics
      const gfx = slot.background.getData('slotGfx') as Phaser.GameObjects.Graphics | undefined;
      if (gfx) {
        const borderColor = (slot.background.getData('borderColor') as number | undefined) ?? 0x4a4a6a;
        const borderAlpha = (slot.background.getData('borderAlpha') as number | undefined) ?? 0.5;
        const x = slot.background.x;
        const y = slot.background.y;
        gfx.clear();
        gfx.lineStyle(1.5, borderColor, borderAlpha);
        gfx.strokeRoundedRect(
          x - BattleScene.SLOT_WIDTH / 2,
          y - BattleScene.SLOT_HEIGHT / 2,
          BattleScene.SLOT_WIDTH,
          BattleScene.SLOT_HEIGHT,
          6
        );
      } else {
        slot.background.setStrokeStyle(1, 0x4a4a6a);
      }
    });
  }

  /**
   * 處理目標選擇
   */
  private handleTargetSelection(cardId: string): boolean {
    if (!this.pendingChoice || this.pendingChoice.type !== 'target') {
      return false;
    }

    if (!this.validTargetIds.has(cardId)) {
      this.showMessage('這不是有效的目標');
      return false;
    }

    // 提交選擇
    const store = useGameStore.getState();
    const result: ChoiceResult = {
      choiceId: this.pendingChoice.id,
      type: 'target',
      selectedCardIds: [cardId],
    };

    const submitResult = store.submitChoice(result);
    if (submitResult.success) {
      this.showMessage('目標已選擇');
      this.clearChoiceHighlights();
    } else {
      this.showMessage(submitResult.error ?? '選擇失敗');
    }

    return submitResult.success;
  }

  private refreshGameState(): void {
    const store = useGameStore.getState();
    this.gameState = store.gameState;
    this.cardDefinitions = store.cardDefinitions;

    if (!this.gameState) return;

    // Check if game has ended - transition to victory screen
    if (this.gameState.gameEnded) {
      this.handleGameEnd();
      return;
    }

    this.updatePhaseDisplay();
    this.updatePlayerInfo();
    this.updateBattlefield();
    this.updateHands();
    this.updateActionButtons();
    this.updateActionHint();

    // Auto-advance draw/defense phases (local mode only)
    this.handleAutoPhases();

    // Check if it's AI's turn
    this.checkAITurn();
  }

  /**
   * Handle game end - show victory message and transition to victory screen
   */
  private handleGameEnd(): void {
    if (!this.gameState) return;

    const winnerId = this.gameState.winnerId;
    const isTie = !winnerId;
    const isPlayerWin = winnerId === this.gameState.player1.id;
    const winnerName = isTie ? '' : (isPlayerWin ? this.gameState.player1.name : this.gameState.player2.name);

    // Stop AI actions
    this.isAITurnActive = false;
    if (this.aiActionTimeout) {
      clearTimeout(this.aiActionTimeout);
      this.aiActionTimeout = null;
    }

    // Show victory message
    this.showVictoryMessage(winnerName, isPlayerWin, isTie);

    // Transition to victory screen after delay
    this.time.delayedCall(3000, () => {
      useUIStore.getState().navigateTo('victory');
    });
  }

  /**
   * Show victory/defeat/tie message overlay
   */
  private showVictoryMessage(winnerName: string, isPlayerWin: boolean, isTie: boolean): void {
    const { width, height } = this.cameras.main;

    // Dark overlay
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7);
    overlay.setDepth(1000);

    // Victory/Defeat/Tie text
    let resultText: string;
    let resultColor: string;
    let subMessage: string;

    if (isTie) {
      resultText = '🤝 平局！';
      resultColor = '#888888';
      subMessage = '雙方勢均力敵';
    } else if (isPlayerWin) {
      resultText = '🎉 勝利！';
      resultColor = '#ffd700';
      subMessage = `${winnerName} 獲勝`;
    } else {
      resultText = '💀 敗北...';
      resultColor = '#ff4444';
      subMessage = `${winnerName} 獲勝`;
    }

    const mainText = this.add.text(width / 2, height / 2 - 40, resultText, {
      fontSize: '64px',
      color: resultColor,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(1001);

    const subText = this.add.text(width / 2, height / 2 + 40, subMessage, {
      fontSize: '32px',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(1001);

    // Animate
    this.tweens.add({
      targets: [mainText, subText],
      scale: { from: 0.5, to: 1 },
      alpha: { from: 0, to: 1 },
      duration: 500,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Auto-advance through draw and defense phases.
   * These phases don't require player input — draw auto-draws cards,
   * defense auto-resolves combat. We add a short delay so the player
   * can see the phase transition animation.
   * Only active in local mode (online mode is server-driven).
   */
  private handleAutoPhases(): void {
    if (!this.gameState) return;

    // Only auto-advance in local mode
    const store = useGameStore.getState();
    if (store.gameMode !== 'local') return;

    // Don't auto-advance if already in progress
    if (this.isAutoAdvancing) return;

    // Don't auto-advance if it's AI's turn (AI handles its own phase transitions)
    if (this.ai && this.gameState.activePlayerId === this.gameState.player2.id) return;

    const phase = this.gameState.currentPhase;

    // Auto-advance draw and defense phases
    if (phase === 'draw' || phase === 'defense') {
      this.isAutoAdvancing = true;

      // Clear any existing timeout
      if (this.autoAdvanceTimeout) {
        clearTimeout(this.autoAdvanceTimeout);
      }

      const delay = phase === 'draw' ? 800 : 600;

      this.autoAdvanceTimeout = setTimeout(() => {
        this.isAutoAdvancing = false;
        this.autoAdvanceTimeout = null;

        // Verify state hasn't changed during the delay
        const currentStore = useGameStore.getState();
        if (!currentStore.gameState) return;
        if (currentStore.gameState.currentPhase !== phase) return;
        if (currentStore.gameMode !== 'local') return;

        const result = currentStore.turnEndPhase();
        if (result.success) {
          log.debug(`Auto-advanced through ${phase} phase`);
        } else {
          log.warn(`Failed to auto-advance ${phase} phase:`, result.error);
        }
      }, delay);
    }
  }

  private checkAITurn(): void {
    if (!this.gameState || !this.ai) return;

    const isPlayer2Turn = this.gameState.activePlayerId === this.gameState.player2.id;

    if (isPlayer2Turn && !this.isAITurnActive) {
      this.isAITurnActive = true;
      this.showMessage('對手回合...');
      this.scheduleAIAction();
    } else if (!isPlayer2Turn) {
      this.isAITurnActive = false;
      if (this.aiActionTimeout) {
        clearTimeout(this.aiActionTimeout);
        this.aiActionTimeout = null;
      }
    }
  }

  private scheduleAIAction(): void {
    if (!this.gameState || !this.ai) return;

    const action = this.ai.getNextAction(this.gameState);
    if (!action) {
      this.isAITurnActive = false;
      return;
    }

    // Schedule the action with delay for visual feedback
    this.aiActionTimeout = setTimeout(() => {
      this.executeAIAction(action);
    }, action.delay);
  }

  private executeAIAction(action: AIAction): void {
    if (!this.gameState) return;

    const store = useGameStore.getState();
    if (!store.gameState) return;

    // Execute the action
    const result = executeAIAction(action, store.gameState);

    if (result.success && result.state) {
      // Update the store with new state
      // We need to manually set the state since executeAIAction returns a new state
      this.updateGameStateFromAI(result.state, action);
    } else {
      log.warn('AI action failed:', result.error);
    }

    // Schedule next action if still AI's turn
    if (this.isAITurnActive) {
      this.scheduleAIAction();
    }
  }

  private updateGameStateFromAI(_newState: GameState, action: AIAction): void {
    // We need to update the store - use a workaround since we don't have direct state setter
    // The cleanest way is to call the appropriate store action based on the AI action type
    const store = useGameStore.getState();

    switch (action.type) {
      case 'draw':
        store.turnExecuteDraw();
        break;
      case 'summon':
        if (action.cardId && action.position) {
          store.turnExecuteSummon(action.cardId, action.position);
          this.showMessage('對手召喚了一個單位');
        }
        break;
      case 'attack':
        if (action.attackerIds) {
          store.turnExecuteAttack(action.attackerIds);
          this.showMessage(`對手宣告 ${String(action.attackerIds.length)} 個單位攻擊！`);
        }
        break;
      case 'endPhase':
        store.turnEndPhase();
        break;
    }
  }

  private static readonly PHASE_CONFIG: Record<TurnPhase, { name: string; color: string; icon: string }> = {
    draw: { name: '抽牌階段', color: '#88ccff', icon: '🃏' },
    defense: { name: '防禦階段', color: '#ff8844', icon: '🛡️' },
    summon: { name: '召喚階段', color: '#ffdd44', icon: '⭐' },
    tactics: { name: '戰術階段', color: '#ff4466', icon: '⚔️' },
    end: { name: '結束階段', color: '#888888', icon: '⏹' },
  };

  // Track active player for turn switch overlay
  private lastActivePlayerId: string | null = null;

  private updatePhaseDisplay(): void {
    if (!this.gameState) return;

    const phase = this.gameState.currentPhase;
    const config = BattleScene.PHASE_CONFIG[phase];

    this.turnText.setText(`第 ${String(this.gameState.turnNumber)} 回合`);
    this.phaseText.setText(`${config.icon} ${config.name}`);
    this.phaseText.setColor(config.color);

    // 階段轉換動畫
    if (this.lastDisplayedPhase !== null && this.lastDisplayedPhase !== phase) {
      this.playPhaseTransition(config.name, config.color, config.icon);
      AudioManager.play('phaseChange');
    }
    this.lastDisplayedPhase = phase;

    // Turn switch overlay
    const isPlayer1Active = this.gameState.activePlayerId === this.gameState.player1.id;
    if (this.lastActivePlayerId !== null && this.lastActivePlayerId !== this.gameState.activePlayerId) {
      this.showTurnSwitchOverlay(isPlayer1Active);
    }
    this.lastActivePlayerId = this.gameState.activePlayerId;

    // Highlight current player
    this.player1Info.setColor(isPlayer1Active ? '#00ff00' : '#ccccee');
    this.player2Info.setColor(!isPlayer1Active ? '#00ff00' : '#ccccee');
  }

  /**
   * Show "Your Turn" / "Opponent's Turn" overlay on turn switch
   */
  private showTurnSwitchOverlay(isYourTurn: boolean): void {
    const { width, height } = this.cameras.main;
    const text = isYourTurn ? '你的回合' : '對手回合';
    const color = isYourTurn ? '#ffd700' : '#ff6666';
    const bgColor = isYourTurn ? 0x3a3010 : 0x3a1010;

    const container = this.add.container(width / 2, height / 2 - 50);
    container.setDepth(1900);

    const bg = this.add.rectangle(0, 0, 300, 70, bgColor, 0.85);
    bg.setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color, 0.8);
    container.add(bg);

    const label = this.add.text(0, 0, text, {
      fontSize: '36px',
      color,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(label);

    // Animate in
    container.setAlpha(0);
    container.setScale(0.7);
    this.tweens.add({
      targets: container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.time.delayedCall(1000, () => {
          this.tweens.add({
            targets: container,
            alpha: 0,
            duration: 400,
            ease: 'Power2',
            onComplete: () => { container.destroy(); },
          });
        });
      },
    });
  }

  /**
   * 播放階段轉換動畫
   */
  private playPhaseTransition(phaseName: string, color: string, icon: string): void {
    // 清理之前的動畫
    if (this.phaseTransitionContainer) {
      this.phaseTransitionContainer.destroy();
      this.phaseTransitionContainer = null;
    }

    const { width, height } = this.cameras.main;
    const centerY = height / 2;

    this.phaseTransitionContainer = this.add.container(-400, centerY);
    this.phaseTransitionContainer.setDepth(1800);

    // 半透明橫幅背景
    const bannerH = 60;
    const bannerW = 360;
    const banner = this.add.rectangle(0, 0, bannerW, bannerH, 0x000000, 0.7);
    banner.setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color);
    this.phaseTransitionContainer.add(banner);

    // 圖示 + 文字
    const displayText = this.add.text(0, 0, `${icon}  ${phaseName}`, {
      fontSize: '28px',
      color,
      fontStyle: 'bold',
      align: 'center',
    });
    displayText.setOrigin(0.5, 0.5);
    this.phaseTransitionContainer.add(displayText);

    // 滑入動畫
    this.tweens.add({
      targets: this.phaseTransitionContainer,
      x: width / 2,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        // 停留後滑出
        this.time.delayedCall(800, () => {
          if (!this.phaseTransitionContainer) return;
          this.tweens.add({
            targets: this.phaseTransitionContainer,
            x: width + 400,
            alpha: 0,
            duration: 300,
            ease: 'Power2',
            onComplete: () => {
              if (this.phaseTransitionContainer) {
                this.phaseTransitionContainer.destroy();
                this.phaseTransitionContainer = null;
              }
            },
          });
        });
      },
    });
  }

  private updatePlayerInfo(): void {
    if (!this.gameState) return;

    const p1 = this.gameState.player1;
    const p2 = this.gameState.player2;

    // Update text info (hidden compatibility)
    this.player1Info.setText(`${p1.name}\n手牌: ${String(p1.hand.length)}`);
    this.player2Info.setText(`${p2.name}\n手牌: ${String(p2.hand.length)}`);

    // 更新手牌數量顯示（含警告色）
    this.updateHandCountDisplay(this.p1HandCountText, p1.hand.length);
    this.updateHandCountDisplay(this.p2HandCountText, p2.hand.length);

    // Update score displays
    const p1Score = Number((p1 as unknown as Record<string, unknown>).score ?? 0);
    const p2Score = Number((p2 as unknown as Record<string, unknown>).score ?? 0);
    this.p1ScoreText.setText(String(p1Score));
    this.p2ScoreText.setText(String(p2Score));

    // Update faction labels
    if (p1.faction) this.p1FactionText.setText(p1.faction);
    if (p2.faction) this.p2FactionText.setText(p2.faction);

    // Update zone slot counts
    this.updateZoneCount(this.p1DeckSlot, p1.deck.length);
    this.updateZoneCount(this.p1GraveyardSlot, p1.graveyard.length);
    this.updateZoneCount(this.p1ForestSlot, p1.forest.length);

    this.updateZoneCount(this.p2DeckSlot, p2.deck.length);
    this.updateZoneCount(this.p2GraveyardSlot, p2.graveyard.length);
    this.updateZoneCount(this.p2ForestSlot, p2.forest.length);
  }

  /**
   * 更新手牌數量顯示，根據數量顯示不同警告色
   * 正常: 白色, 8-9張: 黃色, 10張(滿): 紅色
   */
  private updateHandCountDisplay(text: Phaser.GameObjects.Text, handSize: number): void {
    text.setText(`🃏 ${String(handSize)}/${String(HAND_SIZE_LIMIT)}`);

    if (handSize >= HAND_SIZE_LIMIT) {
      text.setColor('#ff4444');
    } else if (handSize >= HAND_SIZE_LIMIT - 2) {
      text.setColor('#ffcc00');
    } else {
      text.setColor('#aaaaaa');
    }
  }

  private updateZoneCount(container: Phaser.GameObjects.Container, count: number): void {
    const countText = container.getData('countText') as Phaser.GameObjects.Text | undefined;
    if (countText) {
      countText.setText(String(count));
    }
  }

  /**
   * 更新可執行動作提示
   */
  private updateActionHint(): void {
    if (!this.gameState) {
      this.actionHintText.setText('');
      return;
    }

    const isPlayer1Active = this.gameState.activePlayerId === this.gameState.player1.id;

    // 如果不是玩家回合，不顯示提示
    if (!isPlayer1Active) {
      this.actionHintText.setText('');
      return;
    }

    const phase = this.gameState.currentPhase;
    const p1 = this.gameState.player1;

    // 根據階段顯示不同的提示
    switch (phase) {
      case 'draw':
        this.actionHintText.setText('🃏 抽牌階段 - 自動抽牌中...');
        break;
      case 'defense':
        this.actionHintText.setText('🛡️ 防禦階段 - 結算戰鬥中...');
        break;
      case 'summon': {
        const hasCards = p1.hand.length > 0;
        const hasEmptySlots = this.hasEmptyPlayerSlots();
        if (hasCards && hasEmptySlots) {
          this.actionHintText.setText('⭐ 選擇手牌召喚到戰場');
        } else if (!hasCards) {
          this.actionHintText.setText('📭 沒有手牌可召喚，按結束回合繼續');
        } else {
          this.actionHintText.setText('🚫 戰場已滿，按結束回合繼續');
        }
        break;
      }
      case 'tactics': {
        const hasUnits = this.hasPlayerUnitsOnField();
        const hasHandCards = p1.hand.length > 0;
        if (hasUnits || hasHandCards) {
          this.actionHintText.setText('⚔️ 選擇單位攻擊或戰術配置');
        } else {
          this.actionHintText.setText('⏭️ 按結束回合繼續');
        }
        break;
      }
      case 'end':
        this.actionHintText.setText('⏹ 回合結束');
        break;
      default:
        this.actionHintText.setText('');
    }
  }

  /**
   * 檢查玩家是否有空的戰場槽位
   */
  private hasEmptyPlayerSlots(): boolean {
    for (const slot of this.slots.values()) {
      if (slot.position.side === 'player1' && !slot.cardSprite) {
        return true;
      }
    }
    return false;
  }

  /**
   * 檢查玩家是否有單位在戰場上
   */
  private hasPlayerUnitsOnField(): boolean {
    for (const slot of this.slots.values()) {
      if (slot.position.side === 'player1' && slot.cardSprite) {
        return true;
      }
    }
    return false;
  }

  private updateBattlefield(): void {
    if (!this.gameState) return;

    const battlefield = this.gameState.battlefield;

    // Clear existing card sprites from slots
    this.slots.forEach((slot) => {
      if (slot.cardSprite) {
        slot.cardSprite.destroy();
        slot.cardSprite = null;
      }
    });

    // Place cards for player 1
    [...battlefield.player1Side.frontLine, ...battlefield.player1Side.backLine].forEach((slot) => {
      if (slot.unit) {
        this.placeCardInSlot(slot.unit, slot.position);
      }
    });

    // Place cards for player 2
    [...battlefield.player2Side.frontLine, ...battlefield.player2Side.backLine].forEach((slot) => {
      if (slot.unit) {
        this.placeCardInSlot(slot.unit, slot.position);
      }
    });
  }

  private placeCardInSlot(card: Card, position: BattlePosition, animate = false): void {
    const slotKey = this.getSlotKey(position);
    const slotUI = this.slots.get(slotKey);
    if (!slotUI) return;

    const definition = this.cardDefinitions.get(card.definitionId);
    if (!definition) return;

    // Battlefield card scale (optimized for 1920x1080 slot size)
    const bfScale = useUISettingsStore.getState().battlefieldScale;
    const cardSprite = new CardSprite({
      scene: this,
      x: slotUI.background.x,
      y: slotUI.background.y,
      card,
      definition,
      scale: 1.0 * bfScale,
      interactive: true,
    });

    cardSprite.on('pointerdown', () => { this.onCardClick(card, position); });
    cardSprite.on('pointerover', () => {
      if (cardSprite.getData('isDragging')) return;
      this.showCardPreview(card, slotUI.background.x, slotUI.background.y);
    });
    cardSprite.on('pointerout', () => {
      if (cardSprite.getData('isDragging')) return;
      this.hideCardPreview();
    });

    // Enable drag-to-attack for player1's battlefield units during tactics phase
    if (position.side === 'player1') {
      cardSprite.setData('battlefieldCard', true);
      cardSprite.setData('battlefieldPosition', position);
      cardSprite.setData('card', card);
      cardSprite.setData('originalBattleX', slotUI.background.x);
      cardSprite.setData('originalBattleY', slotUI.background.y);
      this.input.setDraggable(cardSprite);
    }

    slotUI.cardSprite = cardSprite;

    // 召喚入場動畫
    if (animate || card.summonedThisTurn) {
      this.playSummonAnimation(cardSprite, definition.rarity);
    }
  }

  /**
   * 播放召喚入場動畫
   * 稀有度越高特效越明顯：
   * - N(普通): 基本縮放 + 淡入
   * - R(稀有): 加光暈效果
   * - SR/SSR/SSSR(傳說): 加光暈 + 粒子效果
   */
  private playSummonAnimation(cardSprite: CardSprite, rarity?: string): void {
    // 基本入場動畫：scale 0→1 + alpha 0→1 + 彈跳
    cardSprite.setScale(0.3);
    cardSprite.setAlpha(0);

    this.tweens.add({
      targets: cardSprite,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });

    // 輕微的螢幕震動
    this.cameras.main.shake(80, 0.003);

    // 稀有度光暈效果
    if (rarity && rarity !== 'N') {
      const glowColor = this.getRarityGlowColor(rarity);
      const glowAlpha = rarity === 'R' ? 0.3 : 0.5;
      const glowSize = rarity === 'R' ? 8 : 14;

      const glow = this.add.rectangle(
        cardSprite.x,
        cardSprite.y,
        CardSprite.CARD_WIDTH + glowSize * 2,
        CardSprite.CARD_HEIGHT + glowSize * 2,
        glowColor,
        0
      );
      glow.setDepth(cardSprite.depth - 1);

      // 光暈淡入再淡出
      this.tweens.add({
        targets: glow,
        alpha: { from: 0, to: glowAlpha },
        duration: 300,
        delay: 100,
        yoyo: true,
        hold: 200,
        onComplete: () => { glow.destroy(); },
      });
    }

    // 傳說級粒子效果（SR 以上）
    if (rarity && ['SR', 'SSR', 'SSSR'].includes(rarity)) {
      this.playSummonParticles(cardSprite.x, cardSprite.y, rarity);
    }
  }

  /**
   * 取得稀有度對應的光暈顏色
   */
  private getRarityGlowColor(rarity: string): number {
    switch (rarity) {
      case 'R': return 0x4488ff;   // 藍色
      case 'SR': return 0xaa44ff;  // 紫色
      case 'SSR': return 0xffd700; // 金色
      case 'SSSR': return 0xffd700; // 金色（更強）
      default: return 0xffffff;
    }
  }

  /**
   * 播放召喚粒子效果（SR 以上稀有度）
   */
  private playSummonParticles(x: number, y: number, rarity: string): void {
    const color = this.getRarityGlowColor(rarity);
    const particleCount = rarity === 'SSSR' ? 16 : rarity === 'SSR' ? 12 : 8;

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const radius = 60 + Math.random() * 20;
      const targetX = x + Math.cos(angle) * radius;
      const targetY = y + Math.sin(angle) * radius;
      const size = 3 + Math.random() * 3;

      const particle = this.add.rectangle(x, y, size, size, color, 0.8);
      particle.setDepth(1500);

      this.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: { from: 1, to: 0.2 },
        duration: 500 + Math.random() * 300,
        delay: 100 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => { particle.destroy(); },
      });
    }
  }

  private updateHands(): void {
    if (!this.gameState) return;

    // Clear existing hand cards
    this.player1HandCards.forEach((card) => { card.destroy(); });
    this.player2HandCards.forEach((card) => { card.destroy(); });
    this.player1HandCards = [];
    this.player2HandCards = [];

    const { height } = this.cameras.main;

    // Player 1 hand (bottom) - Hearthstone-style arc layout, EXTRA LARGE cards
    const p1Hand = this.gameState.player1.hand;
    const handY = height - 50;  // Position at bottom, cards extend below screen slightly
    this.renderHand(p1Hand, handY, this.player1HandCards, false);

    // Player 2 hand (top) - face down for opponent
    const p2Hand = this.gameState.player2.hand;
    this.renderHand(p2Hand, 80, this.player2HandCards, true);
  }

  private renderHand(hand: Card[], baseY: number, container: CardSprite[], faceDown: boolean): void {
    const { width } = this.cameras.main;
    const cardCount = hand.length;
    if (cardCount === 0) return;

    // Hearthstone-style arc layout - 自動縮小以適應手牌數量
    const uiHandScale = useUISettingsStore.getState().handScale;
    let handScale = faceDown ? 0.65 : 1.1 * uiHandScale;  // Player cards much larger, apply user scale
    let maxSpread = Math.min(cardCount * 130, 900);  // Wider horizontal spread
    const arcHeight = faceDown ? 8 : 25;  // How much cards curve up at edges
    const maxRotation = faceDown ? 6 : 15;  // Less rotation for larger cards

    // 手牌過多時自動縮小以避免超出畫面
    if (!faceDown && cardCount > 7) {
      const scaleFactor = Math.max(0.5, 6 / cardCount);
      handScale = handScale * scaleFactor;
      maxSpread = Math.min(cardCount * 100 * scaleFactor, width - 300);
    } else if (faceDown && cardCount > 8) {
      const scaleFactor = Math.max(0.5, 8 / cardCount);
      handScale = handScale * scaleFactor;
      maxSpread = Math.min(cardCount * 80 * scaleFactor, width - 300);
    }

    // Calculate positions for arc effect
    const centerX = width / 2;

    hand.forEach((card, index) => {
      const definition = this.cardDefinitions.get(card.definitionId);
      if (!definition) return;

      // Calculate position along the arc
      const t = cardCount === 1 ? 0.5 : index / (cardCount - 1);  // 0 to 1
      const normalizedT = t - 0.5;  // -0.5 to 0.5

      // X position: spread cards horizontally
      const x = centerX + normalizedT * maxSpread;

      // Y position: arc curve (cards at edges are higher)
      const arcOffset = Math.pow(normalizedT * 2, 2) * arcHeight;
      const y = baseY + arcOffset;

      // Rotation: cards at edges rotate outward
      const rotation = normalizedT * maxRotation * 2;

      const cardSprite = new CardSprite({
        scene: this,
        x,
        y,
        card,
        definition,
        scale: handScale,
        faceDown,
        interactive: !faceDown,
      });

      cardSprite.setAngle(rotation);
      cardSprite.setDepth(100 + index);  // Cards on right are on top

      // Store original position for hover effects
      cardSprite.setData('originalY', y);
      cardSprite.setData('originalAngle', rotation);
      cardSprite.setData('originalScale', handScale);
      cardSprite.setData('index', index);

      // 可出牌時加綠色發光邊框
      if (!faceDown &&
          this.gameState?.currentPhase === 'summon' &&
          this.gameState.activePlayerId === this.gameState.player1.id) {
        cardSprite.highlight(0x00ff00);
      }

      if (!faceDown) {
        // Enable drag
        this.input.setDraggable(cardSprite);

        // Store card data for drag operations
        cardSprite.setData('card', card);
        cardSprite.setData('isDragging', false);

        cardSprite.on('pointerdown', () => {
          if (!cardSprite.getData('isDragging')) {
            this.onHandCardClick(card);
          }
        });
        cardSprite.on('pointerover', () => {
          if (cardSprite.getData('isDragging')) return;
          // Smoothly lift card up and enlarge (Hearthstone style)
          // Reduced lift distance to prevent card from escaping cursor during drag
          this.tweens.add({
            targets: cardSprite,
            y: baseY - 80,
            angle: 0,
            scaleX: handScale * 1.15,
            scaleY: handScale * 1.15,
            duration: 120,
            ease: 'Power2',
          });
          cardSprite.setDepth(500);
          this.showCardPreview(card, cardSprite.x, baseY - 220);
        });
        cardSprite.on('pointerout', () => {
          if (cardSprite.getData('isDragging')) return;
          this.hideCardPreview();
          const origY = cardSprite.getData('originalY') as number;
          const origAngle = cardSprite.getData('originalAngle') as number;
          const origScale = cardSprite.getData('originalScale') as number;
          const origIndex = cardSprite.getData('index') as number;

          if (this.selectedCardId !== card.id) {
            this.tweens.add({
              targets: cardSprite,
              y: origY,
              angle: origAngle,
              scaleX: origScale,
              scaleY: origScale,
              duration: 150,
              ease: 'Power2',
            });
          }
          cardSprite.setDepth(100 + origIndex);
        });
      }

      container.push(cardSprite);
    });
  }

  /**
   * 設置拖曳系統 (Hearthstone-style drag-and-drop)
   */
  private dragStartPos: { x: number; y: number } | null = null;

  private setupDragAndDrop(): void {
    // Require 8px drag distance before starting drag (prevents accidental drags)
    this.input.dragDistanceThreshold = 8;

    this.input.on('dragstart', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Container) => {
      const isBattlefieldCard = gameObject.getData('battlefieldCard') as boolean | undefined;
      gameObject.setData('isDragging', true);
      this.hideCardPreview();

      // Kill any active hover tweens on this card to prevent it from flying away
      this.tweens.killTweensOf(gameObject);

      this.dragStartPos = { x: gameObject.x, y: gameObject.y };

      if (isBattlefieldCard && this.gameState?.currentPhase === 'tactics') {
        // Battlefield drag-to-attack: don't move the card, just show arrow
        gameObject.setData('attackDragging', true);

        // Create attack arrow graphics
        if (!this.attackArrow) {
          this.attackArrow = this.add.graphics();
          this.attackArrow.setDepth(900);
        }
      } else {
        // Hand card drag: lift and shrink
        gameObject.setDepth(1000);
        gameObject.setAlpha(0.85);
        gameObject.setAngle(0);
        const origScale = gameObject.getData('originalScale') as number | undefined;
        if (origScale) gameObject.setScale(origScale * 0.9);

        // Highlight valid drop targets (empty slots on player's side during summon phase)
        if (this.gameState?.currentPhase === 'summon') {
          this.highlightValidSlots();
        }
      }
    });

    this.input.on('drag', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Container, dragX: number, dragY: number) => {
      const isAttackDrag = gameObject.getData('attackDragging') as boolean | undefined;

      if (isAttackDrag) {
        // Don't move the card — draw arrow from card to cursor
        if (this.attackArrow) {
          this.attackArrow.clear();
          const startX = this.dragStartPos?.x ?? gameObject.x;
          const startY = this.dragStartPos?.y ?? gameObject.y;
          // Red arrow line
          this.attackArrow.lineStyle(4, 0xff4444, 0.7);
          this.attackArrow.lineBetween(startX, startY, dragX, dragY);
          // Arrowhead
          const angle = Math.atan2(dragY - startY, dragX - startX);
          const arrowSize = 14;
          this.attackArrow.fillStyle(0xff4444, 0.9);
          this.attackArrow.fillTriangle(
            dragX,
            dragY,
            dragX - arrowSize * Math.cos(angle - 0.4),
            dragY - arrowSize * Math.sin(angle - 0.4),
            dragX - arrowSize * Math.cos(angle + 0.4),
            dragY - arrowSize * Math.sin(angle + 0.4)
          );
        }
      } else {
        // Hand card drag: move the card
        gameObject.x = dragX;
        gameObject.y = dragY;
        gameObject.setAngle(0);
      }
    });

    this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Container) => {
      const isAttackDrag = gameObject.getData('attackDragging') as boolean | undefined;
      gameObject.setData('isDragging', false);
      gameObject.setData('attackDragging', false);
      gameObject.setAlpha(1);
      this.tweens.killTweensOf(gameObject);

      // Clear attack arrow
      if (this.attackArrow) {
        this.attackArrow.clear();
      }

      // Handle battlefield drag-to-attack
      if (isAttackDrag) {
        const card = gameObject.getData('card') as Card | undefined;
        if (card && this.gameState?.currentPhase === 'tactics') {
          // Check if released over an enemy slot/card
          const targetSlot = this.getAnySlotAtPosition(pointer.x, pointer.y);
          if (targetSlot && targetSlot.side !== 'player1') {
            // Execute single-unit attack
            const store = useGameStore.getState();
            if (store.gameMode === 'online') {
              sendGameCommand('declare_attack', { card_ids: [card.id] });
              AudioManager.play('attack');
            } else {
              const attackResult = store.turnExecuteAttack([card.id]);
              if (attackResult.success) {
                this.showMessage('⚔️ 宣告攻擊！');
                AudioManager.play('attack');
              } else {
                this.showMessage(attackResult.error ?? '無法攻擊');
              }
            }
          }
        }
        // Snap battlefield card back (it didn't move, but ensure position)
        const origBX = gameObject.getData('originalBattleX') as number | undefined;
        const origBY = gameObject.getData('originalBattleY') as number | undefined;
        if (origBX !== undefined && origBY !== undefined) {
          gameObject.x = origBX;
          gameObject.y = origBY;
        }
        this.dragStartPos = null;
        return;
      }

      // Check if dropped on a valid slot (hand card summon)
      const card = gameObject.getData('card') as Card | undefined;
      const droppedSlot = this.getSlotAtPosition(pointer.x, pointer.y);

      if (card && droppedSlot && this.gameState?.currentPhase === 'summon') {
        // Attempt summon at this position
        const store = useGameStore.getState();
        const result = store.turnExecuteSummon(card.id, droppedSlot);
        if (result.success) {
          this.selectedCardId = null;
          this.clearHighlights();
          // Card will be removed from hand on next state update
          return;
        }
      }

      // Return hand card to original position (snap back)
      const origY = gameObject.getData('originalY') as number;
      const origAngle = gameObject.getData('originalAngle') as number;
      const origScale = gameObject.getData('originalScale') as number;
      const origIndex = gameObject.getData('index') as number;

      this.tweens.add({
        targets: gameObject,
        x: this.dragStartPos?.x ?? gameObject.x,
        y: origY,
        angle: origAngle,
        scaleX: origScale,
        scaleY: origScale,
        duration: 200,
        ease: 'Back.easeOut',
      });
      gameObject.setDepth(100 + origIndex);

      this.clearHighlights();
      this.dragStartPos = null;
    });
  }

  /**
   * 找到指定螢幕座標下的戰場格子
   */
  private getSlotAtPosition(screenX: number, screenY: number): BattlePosition | null {
    const camera = this.cameras.main;
    const gameX = (screenX - camera.x) / camera.zoom + camera.scrollX;
    const gameY = (screenY - camera.y) / camera.zoom + camera.scrollY;

    for (const [, slot] of this.slots.entries()) {
      const bg = slot.background;
      const halfW = BattleScene.SLOT_WIDTH / 2;
      const halfH = BattleScene.SLOT_HEIGHT / 2;

      if (gameX >= bg.x - halfW && gameX <= bg.x + halfW &&
          gameY >= bg.y - halfH && gameY <= bg.y + halfH) {
        // Only allow dropping on player1's empty slots
        if (slot.position.side === 'player1' && !slot.cardSprite) {
          return slot.position;
        }
      }
    }
    return null;
  }

  /**
   * 找到任意戰場格子（不限 side 和 occupied 狀態），用於拖移攻擊目標偵測
   */
  private getAnySlotAtPosition(screenX: number, screenY: number): BattlePosition | null {
    const camera = this.cameras.main;
    const gameX = (screenX - camera.x) / camera.zoom + camera.scrollX;
    const gameY = (screenY - camera.y) / camera.zoom + camera.scrollY;

    for (const [, slot] of this.slots.entries()) {
      const bg = slot.background;
      const halfW = BattleScene.SLOT_WIDTH / 2;
      const halfH = BattleScene.SLOT_HEIGHT / 2;

      if (gameX >= bg.x - halfW && gameX <= bg.x + halfW &&
          gameY >= bg.y - halfH && gameY <= bg.y + halfH) {
        return slot.position;
      }
    }
    return null;
  }

  /**
   * 高亮所有可放置卡牌的空格子
   */
  private highlightValidSlots(): void {
    for (const [, slot] of this.slots.entries()) {
      if (slot.position.side === 'player1' && !slot.cardSprite) {
        const gfx = slot.background.getData('slotGfx') as Phaser.GameObjects.Graphics | undefined;
        if (gfx) {
          const x = slot.background.x;
          const y = slot.background.y;
          gfx.clear();
          gfx.lineStyle(2, 0x00ff00, 0.8);
          gfx.strokeRoundedRect(
            x - BattleScene.SLOT_WIDTH / 2,
            y - BattleScene.SLOT_HEIGHT / 2,
            BattleScene.SLOT_WIDTH,
            BattleScene.SLOT_HEIGHT,
            6
          );
          gfx.fillStyle(0x004400, 0.3);
          gfx.fillRoundedRect(
            x - BattleScene.SLOT_WIDTH / 2,
            y - BattleScene.SLOT_HEIGHT / 2,
            BattleScene.SLOT_WIDTH,
            BattleScene.SLOT_HEIGHT,
            6
          );
        } else {
          slot.background.setStrokeStyle(2, 0x00ff00, 0.8);
          slot.background.setFillStyle(0x004400, 0.3);
        }
      }
    }
  }

  /**
   * 顯示卡牌懸停預覽（放大 + 詳細資訊）
   */
  private showCardPreview(card: Card, originX: number, originY: number): void {
    this.hideCardPreview();

    const definition = this.cardDefinitions.get(card.definitionId);
    if (!definition) return;

    const { width, height } = this.cameras.main;
    const previewScale = 2.2;
    const cardW = CardSprite.CARD_WIDTH * previewScale;
    const cardH = CardSprite.CARD_HEIGHT * previewScale;

    // 決定預覽位置：避免超出螢幕邊界
    // 預設放在滑鼠位置的右側，如果靠右邊就放左側
    let px = originX + 130;
    const py = Math.max(cardH / 2 + 10, Math.min(originY, height - cardH / 2 - 10));
    if (px + cardW / 2 > width - 20) {
      px = originX - 130;
    }
    // 確保不超出左邊界
    if (px - cardW / 2 < 20) {
      px = cardW / 2 + 20;
    }

    this.previewContainer = this.add.container(px, py);
    this.previewContainer.setDepth(2000);

    // 半透明背景
    const bgPadding = 10;
    const infoPanelH = definition.type === 'unit' ? 130 : 100;
    const totalH = cardH + infoPanelH + bgPadding * 2;
    const totalW = cardW + bgPadding * 2;

    const bg = this.add.rectangle(0, (infoPanelH) / 2, totalW, totalH, 0x000000, 0.85);
    bg.setStrokeStyle(2, 0x666688);
    this.previewContainer.add(bg);

    // 放大的卡牌圖
    const cardSprite = new CardSprite({
      scene: this,
      x: 0,
      y: -infoPanelH / 2,
      card,
      definition,
      scale: previewScale,
    });
    this.previewContainer.add(cardSprite);

    // 資訊面板
    const infoY = cardH / 2 - infoPanelH / 2 + 15;
    const textX = -cardW / 2 + 8;

    // 卡名
    const nameText = this.add.text(0, infoY, definition.nameZh || definition.name, {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
      align: 'center',
    });
    nameText.setOrigin(0.5, 0);
    this.previewContainer.add(nameText);

    // 陣營 + 稀有度
    const factionText = definition.faction;
    const rarityText = definition.rarity ? ` (${definition.rarity})` : '';
    const subtitleText = this.add.text(0, infoY + 22, `${factionText}${rarityText}`, {
      fontSize: '12px',
      color: '#aaaaaa',
      align: 'center',
    });
    subtitleText.setOrigin(0.5, 0);
    this.previewContainer.add(subtitleText);

    // ATK / Score (for units)
    if (definition.type === 'unit') {
      const statsStr = `ATK ${String(definition.atk ?? 0)}  |  分數 ${String(definition.score ?? 0)}`;
      const atkText = this.add.text(0, infoY + 40, statsStr, {
        fontSize: '14px',
        color: '#ffdd44',
        fontStyle: 'bold',
        align: 'center',
      });
      atkText.setOrigin(0.5, 0);
      this.previewContainer.add(atkText);
    }

    // 效果文字
    if (definition.effectText) {
      const effectY = definition.type === 'unit' ? infoY + 60 : infoY + 40;
      const effectText = this.add.text(textX, effectY, definition.effectText, {
        fontSize: '11px',
        color: '#cccccc',
        wordWrap: { width: cardW - 16 },
        lineSpacing: 2,
      });
      effectText.setOrigin(0, 0);
      this.previewContainer.add(effectText);
    }

    // 淡入動畫
    this.previewContainer.setAlpha(0);
    this.tweens.add({
      targets: this.previewContainer,
      alpha: 1,
      duration: 120,
      ease: 'Power2',
    });
  }

  /**
   * 隱藏卡牌預覽
   */
  private hideCardPreview(): void {
    if (this.previewHideTimer) {
      clearTimeout(this.previewHideTimer);
      this.previewHideTimer = null;
    }
    if (this.previewContainer) {
      this.previewContainer.destroy();
      this.previewContainer = null;
    }
  }

  /**
   * 設定區域容器的點擊事件
   */
  private setupZoneClickHandler(container: Phaser.GameObjects.Container, onClick: () => void): void {
    // 取得容器中的第一個子元素（背景矩形）作為互動區域
    const bg = container.getAt(0);
    bg.setInteractive({ useHandCursor: true });
    (bg as Phaser.GameObjects.Rectangle).on('pointerdown', onClick);
  }

  /**
   * 開啟區域瀏覽器（墓地/牌庫/森林）
   */
  private openZoneBrowser(title: string, cards: Card[], isDeck = false): void {
    this.closeZoneBrowser();

    if (cards.length === 0) {
      this.showMessage(`${title} 是空的`);
      return;
    }

    const { width, height } = this.cameras.main;

    this.zoneBrowserContainer = this.add.container(width / 2, height / 2);
    this.zoneBrowserContainer.setDepth(3000);

    // 半透明全螢幕背景（點擊關閉）
    const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.75);
    overlay.setInteractive();
    overlay.on('pointerdown', () => { this.closeZoneBrowser(); });
    this.zoneBrowserContainer.add(overlay);

    // 面板
    const panelW = Math.min(width - 100, 900);
    const panelH = Math.min(height - 100, 600);
    const panel = this.add.rectangle(0, 0, panelW, panelH, 0x1a1a2e, 0.95);
    panel.setStrokeStyle(2, 0x6666aa);
    this.zoneBrowserContainer.add(panel);

    // 標題
    const titleText = this.add.text(0, -panelH / 2 + 20, `${title} (${String(cards.length)} 張)`, {
      fontSize: '22px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.zoneBrowserContainer.add(titleText);

    // 關閉按鈕
    const closeBtn = this.add.text(panelW / 2 - 20, -panelH / 2 + 15, '✕', {
      fontSize: '24px',
      color: '#ff6666',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => { this.closeZoneBrowser(); });
    closeBtn.on('pointerover', () => { closeBtn.setColor('#ff9999'); });
    closeBtn.on('pointerout', () => { closeBtn.setColor('#ff6666'); });
    this.zoneBrowserContainer.add(closeBtn);

    // 卡牌列表
    const cardAreaTop = -panelH / 2 + 55;
    const cardScale = 0.8;
    const cardW = CardSprite.CARD_WIDTH * cardScale;
    const cardH = CardSprite.CARD_HEIGHT * cardScale;
    const gap = 10;
    const cols = Math.floor((panelW - 40) / (cardW + gap));
    const startX = -((cols - 1) * (cardW + gap)) / 2;

    // 牌庫只顯示數量，不顯示內容（隱藏資訊）
    if (isDeck) {
      const infoText = this.add.text(0, 0, `剩餘 ${String(cards.length)} 張`, {
        fontSize: '32px',
        color: '#aaaacc',
        fontStyle: 'bold',
      }).setOrigin(0.5, 0.5);
      this.zoneBrowserContainer.add(infoText);
      return;
    }

    // 顯示卡牌（墓地、森林等）
    const maxDisplay = Math.min(cards.length, cols * 4); // 最多 4 行
    for (let i = 0; i < maxDisplay; i++) {
      const card = cards[i];
      const definition = this.cardDefinitions.get(card.definitionId);
      if (!definition) continue;

      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = startX + col * (cardW + gap);
      const cy = cardAreaTop + row * (cardH + gap) + cardH / 2;

      const cardSprite = new CardSprite({
        scene: this,
        x: cx,
        y: cy,
        card,
        definition,
        scale: cardScale,
        interactive: true,
      });

      // 懸停預覽
      cardSprite.on('pointerover', () => {
        this.showCardPreview(card, width / 2 + cx + 80, height / 2 + cy);
      });
      cardSprite.on('pointerout', () => {
        this.hideCardPreview();
      });

      this.zoneBrowserContainer.add(cardSprite);
    }

    // 如果有更多卡牌
    if (cards.length > maxDisplay) {
      const moreText = this.add.text(0, cardAreaTop + 4 * (cardH + gap) + 10, `...還有 ${String(cards.length - maxDisplay)} 張`, {
        fontSize: '14px',
        color: '#888888',
      }).setOrigin(0.5, 0);
      this.zoneBrowserContainer.add(moreText);
    }

    // 淡入動畫
    this.zoneBrowserContainer.setAlpha(0);
    this.tweens.add({
      targets: this.zoneBrowserContainer,
      alpha: 1,
      duration: 200,
      ease: 'Power2',
    });
  }

  /**
   * 關閉區域瀏覽器
   */
  private closeZoneBrowser(): void {
    this.hideCardPreview();
    if (this.zoneBrowserContainer) {
      this.zoneBrowserContainer.destroy();
      this.zoneBrowserContainer = null;
    }
  }

  private updateActionButtons(): void {
    if (!this.gameState) return;

    const isPlayer1 = this.gameState.activePlayerId === this.gameState.player1.id;

    // Update end turn button: visible always, but dimmed when not your turn
    this.endTurnButton.setAlpha(isPlayer1 ? 1 : 0.35);
    const btnBg = this.endTurnButton.getData('bg') as Phaser.GameObjects.Rectangle | undefined;
    if (btnBg) {
      if (isPlayer1) {
        btnBg.setInteractive({ useHandCursor: true });
      } else {
        btnBg.disableInteractive();
      }
    }

    // Pulse only when it's your turn
    if (this.endTurnPulseTween) {
      if (isPlayer1) {
        this.endTurnPulseTween.resume();
      } else {
        this.endTurnPulseTween.pause();
        const glow = this.endTurnButton.getData('glow') as Phaser.GameObjects.Rectangle | undefined;
        if (glow) glow.setAlpha(0);
      }
    }

    // Surrender button only visible in online mode
    const store = useGameStore.getState();
    this.surrenderButton.setVisible(store.gameMode === 'online');

    // Reposition button: visible only during tactics phase when it's player1's turn
    const phase = this.gameState.currentPhase;
    const showReposition = phase === 'tactics' && isPlayer1 && store.gameMode !== 'online';
    this.repositionButton.setVisible(showReposition);

    // Move reposition button below surrender or end turn
    if (showReposition) {
      const surrenderVisible = store.gameMode === 'online';
      const yOffset = surrenderVisible ? 60 : 20;
      const { height } = this.cameras.main;
      this.repositionButton.setY(height / 2 - 50 + yOffset);
    }

    // Update end turn button text for tactics phase
    this.updateEndTurnButtonForTactics();

    // Reset reposition mode when leaving tactics
    if (phase !== 'tactics') {
      this.repositionModeActive = false;
      const uiStore = useUIStore.getState();
      if (uiStore.unitMoveSource) {
        uiStore.setUnitMoveSource(null);
      }
    }
  }

  private onHandCardClick(card: Card): void {
    log.debug('onHandCardClick:', card.id);
    
    if (!this.gameState) {
      log.debug('No game state');
      return;
    }

    const phase = this.gameState.currentPhase;
    const isPlayer1Active = this.gameState.activePlayerId === this.gameState.player1.id;

    log.debug('Phase:', phase, 'isPlayer1Active:', isPlayer1Active);

    if (!isPlayer1Active) {
      log.debug('Not player 1 turn');
      return;
    }

    // Toggle selection
    if (this.selectedCardId === card.id) {
      this.selectedCardId = null;
      this.showMessage('');
      log.debug('Deselected card');
    } else {
      this.selectedCardId = card.id;
      AudioManager.play('drawCard');
      log.debug('Selected card:', card.id);

      if (phase === 'summon' || phase === 'tactics') {
        this.showMessage('選擇一個空位放置卡牌');
        this.highlightEmptySlots();
      }
    }

    this.updateHandSelection();
  }

  private onCardClick(card: Card, position: BattlePosition): void {
    if (!this.gameState) return;

    // 優先處理目標選擇
    if (this.pendingChoice && this.pendingChoice.type === 'target') {
      this.handleTargetSelection(card.id);
      return;
    }

    const phase = this.gameState.currentPhase;
    const isPlayer1Active = this.gameState.activePlayerId === this.gameState.player1.id;

    if (!isPlayer1Active) return;

    if (phase === 'tactics') {
      const uiStore = useUIStore.getState();

      if (position.side === 'player1') {
        // Reposition mode: handle unit swap/move
        if (this.repositionModeActive) {
          if (uiStore.unitMoveSource) {
            const from = uiStore.unitMoveSource;
            // Same position = cancel
            if (from.row === position.row && from.column === position.column && from.side === position.side) {
              uiStore.setUnitMoveSource(null);
              this.repositionModeActive = false;
              this.clearHighlights();
              this.showMessage('');
              (this.repositionButton.getData('text') as Phaser.GameObjects.Text).setText('🔄 調整位置');
            } else {
              // Execute swap
              const store = useGameStore.getState();
              const result = store.turnExecuteSwap(from, position);
              if (result.success) {
                this.showMessage('單位位置交換成功！');
                AudioManager.play('playCard');
              } else {
                this.showMessage(result.error ?? '無法交換');
              }
              uiStore.setUnitMoveSource(null);
              this.repositionModeActive = false;
              this.clearHighlights();
              (this.repositionButton.getData('text') as Phaser.GameObjects.Text).setText('🔄 調整位置');
            }
          } else {
            // First click in reposition mode: select the unit to move
            uiStore.setUnitMoveSource(position);
            this.clearHighlights();
            // Highlight the selected unit and all valid destinations
            const slotKey = this.getSlotKey(position);
            const slotUI = this.slots.get(slotKey);
            if (slotUI?.cardSprite) {
              slotUI.cardSprite.highlight(0xffaa00); // orange for selected
            }
            // Highlight all player1 slots as valid destinations
            this.slots.forEach((slot) => {
              if (slot.position.side === 'player1') {
                const gfx = slot.background.getData('slotGfx') as Phaser.GameObjects.Graphics | undefined;
                if (gfx) {
                  const x = slot.background.x;
                  const y = slot.background.y;
                  gfx.clear();
                  gfx.lineStyle(2.5, 0x66cc88, 0.9);
                  gfx.strokeRoundedRect(
                    x - BattleScene.SLOT_WIDTH / 2,
                    y - BattleScene.SLOT_HEIGHT / 2,
                    BattleScene.SLOT_WIDTH,
                    BattleScene.SLOT_HEIGHT,
                    6
                  );
                }
              }
            });
            this.showMessage('點擊目標位置來移動/交換');
          }
          return;
        }

        // If we have a move source selected (from slot click), this click is the swap target
        if (uiStore.unitMoveSource) {
          const from = uiStore.unitMoveSource;
          // Same position = cancel
          if (from.row === position.row && from.column === position.column && from.side === position.side) {
            uiStore.setUnitMoveSource(null);
            this.clearHighlights();
            this.showMessage('');
          } else {
            // Execute swap
            const store = useGameStore.getState();
            const result = store.turnExecuteSwap(from, position);
            if (result.success) {
              this.showMessage('單位位置交換成功！');
              AudioManager.play('playCard');
            } else {
              this.showMessage(result.error ?? '無法交換');
            }
            uiStore.setUnitMoveSource(null);
            this.clearHighlights();
          }
          return;
        }

        // Normal click: toggle attack selection
        if (uiStore.selectedAttackers.includes(card.id)) {
          uiStore.removeAttacker(card.id);
        } else {
          uiStore.addAttacker(card.id);
        }
        this.updateAttackerHighlights();
        this.updateEndTurnButtonForTactics();
      }
    }
  }

  private onSlotClick(position: BattlePosition): void {
    log.debug('onSlotClick:', position);
    
    if (!this.gameState) {
      log.debug('No game state');
      return;
    }

    const phase = this.gameState.currentPhase;
    const isPlayer1Active = this.gameState.activePlayerId === this.gameState.player1.id;

    if (!isPlayer1Active) {
      log.debug('Not player 1 turn');
      return;
    }

    // Handle unit move/swap destination (empty slot target during tactics)
    if (phase === 'tactics' && position.side === 'player1') {
      const uiStore = useUIStore.getState();
      if (uiStore.unitMoveSource) {
        const from = uiStore.unitMoveSource;
        const store = useGameStore.getState();
        const result = store.turnExecuteSwap(from, position);
        if (result.success) {
          this.showMessage('單位移動成功！');
          AudioManager.play('playCard');
        } else {
          this.showMessage(result.error ?? '無法移動');
        }
        uiStore.setUnitMoveSource(null);
        this.clearHighlights();
        return;
      }
    }
    
    if (!this.selectedCardId) {
      log.debug('No card selected');
      return;
    }

    log.debug('Phase:', phase, 'isPlayer1Active:', isPlayer1Active, 'selectedCard:', this.selectedCardId);

    // Only allow placing on player 1's side
    if (position.side !== 'player1') {
      this.showMessage('只能放置在自己的區域');
      log.debug('Wrong side');
      return;
    }

    const store = useGameStore.getState();

    // 線上模式：發送伺服器指令，等待 state_sync 更新
    if (store.gameMode === 'online') {
      const playerSide = store.onlinePlayerSide;
      if (!playerSide) return;
      const serverPos = localToServerPosition(position, playerSide);
      const cmdType = phase === 'summon' ? 'summon_unit' : 'tactical_summon';
      sendGameCommand(cmdType, {
        card_id: this.selectedCardId,
        position: serverPos,
      });
      this.selectedCardId = null;
      this.clearHighlights();
      AudioManager.play('playCard');
      return;
    }

    if (phase === 'summon') {
      log.debug('Attempting summon...');
      const result = store.turnExecuteSummon(this.selectedCardId, position);
      log.debug('Summon result:', result);
      if (result.success) {
        this.selectedCardId = null;
        this.showMessage('召喚成功！');
        this.clearHighlights();
        AudioManager.play('playCard');
      } else {
        this.showMessage(result.error || '無法召喚');
      }
    } else if (phase === 'tactics') {
      log.debug('Attempting tactical summon...');
      const result = store.turnExecuteTacticalSummon(this.selectedCardId, position);
      log.debug('Tactical summon result:', result);
      if (result.success) {
        this.selectedCardId = null;
        this.showMessage('戰術配置成功！');
        this.clearHighlights();
        AudioManager.play('playCard');
      } else {
        this.showMessage(result.error || '無法配置');
      }
    } else {
      log.debug('Wrong phase for placement');
    }
  }

  private onActionButton(action: string): void {
    const store = useGameStore.getState();
    const uiStore = useUIStore.getState();

    if (action === 'endPhase') {
      // If in tactics phase with selected attackers, treat as attack action
      if (store.gameState?.currentPhase === 'tactics' && uiStore.selectedAttackers.length > 0) {
        this.onActionButton('attack');
        return;
      }

      // 線上模式：發送伺服器指令
      if (store.gameMode === 'online') {
        sendGameCommand('end_phase');
        this.clearHighlights();
        this.selectedCardId = null;
        uiStore.clearAttackers();
        return;
      }
      const result = store.turnEndPhase();
      if (result.success) {
        this.showMessage('進入下一階段');
        this.clearHighlights();
        this.selectedCardId = null;
        uiStore.clearAttackers();
        uiStore.setUnitMoveSource(null);
        this.repositionModeActive = false;
      } else {
        this.showMessage(result.error ?? '無法結束階段');
      }
    } else if (action === 'attack') {
      const attackers = uiStore.selectedAttackers;
      if (attackers.length > 0) {
        // 線上模式：發送伺服器指令
        if (store.gameMode === 'online') {
          sendGameCommand('declare_attack', { card_ids: attackers });
          uiStore.clearAttackers();
          this.updateAttackerHighlights();
          AudioManager.play('attack');
          // 進攻宣言用掉了戰術階段的動作，自動結束階段
          sendGameCommand('end_phase');
          this.clearHighlights();
          this.selectedCardId = null;
          return;
        }
        const attackResult = store.turnExecuteAttack(attackers);
        if (attackResult.success) {
          this.showMessage(`宣告 ${String(attackers.length)} 個單位攻擊！`);
          uiStore.clearAttackers();
          this.updateAttackerHighlights();
          AudioManager.play('attack');
          // 進攻宣言用掉了戰術階段的動作，自動結束階段
          const endResult = store.turnEndPhase();
          if (endResult.success) {
            this.clearHighlights();
            this.selectedCardId = null;
          }
        } else {
          this.showMessage(attackResult.error === 'ACTION_ALREADY_USED'
            ? '本回合已使用過戰術動作'
            : attackResult.error ?? '無法攻擊');
        }
      } else {
        this.showMessage('請先選擇要攻擊的單位');
      }
    } else if (action === 'surrender') {
      if (store.gameMode === 'online') {
        sendGameCommand('surrender');
      }
    } else if (action === 'cancel') {
      this.selectedCardId = null;
      uiStore.clearAttackers();
      this.clearHighlights();
      this.showMessage('');
      this.updateHandSelection();
    }
  }

  private highlightEmptySlots(): void {
    if (!this.gameState) return;

    this.slots.forEach((slot) => {
      if (slot.position.side === 'player1' && !slot.cardSprite) {
        const gfx = slot.background.getData('slotGfx') as Phaser.GameObjects.Graphics | undefined;
        if (gfx) {
          const x = slot.background.x;
          const y = slot.background.y;
          gfx.clear();
          gfx.lineStyle(2.5, 0x00ff00, 0.9);
          gfx.strokeRoundedRect(
            x - BattleScene.SLOT_WIDTH / 2,
            y - BattleScene.SLOT_HEIGHT / 2,
            BattleScene.SLOT_WIDTH,
            BattleScene.SLOT_HEIGHT,
            6
          );
        } else {
          slot.background.setStrokeStyle(2, 0x00ff00);
        }
      }
    });
  }

  private clearHighlights(): void {
    this.slots.forEach((slot) => {
      const gfx = slot.background.getData('slotGfx') as Phaser.GameObjects.Graphics | undefined;
      if (gfx) {
        const borderColor = (slot.background.getData('borderColor') as number | undefined) ?? 0x4a4a6a;
        const borderAlpha = (slot.background.getData('borderAlpha') as number | undefined) ?? 0.5;
        const x = slot.background.x;
        const y = slot.background.y;
        gfx.clear();
        gfx.lineStyle(1.5, borderColor, borderAlpha);
        gfx.strokeRoundedRect(
          x - BattleScene.SLOT_WIDTH / 2,
          y - BattleScene.SLOT_HEIGHT / 2,
          BattleScene.SLOT_WIDTH,
          BattleScene.SLOT_HEIGHT,
          6
        );
      } else {
        slot.background.setStrokeStyle(1, 0x4a4a6a);
      }
    });
  }

  private updateHandSelection(): void {
    const { height } = this.cameras.main;
    const baseY = height - 100;  // Match the hand rendering position

    this.player1HandCards.forEach((cardSprite) => {
      const origY = cardSprite.getData('originalY') as number;
      const origAngle = cardSprite.getData('originalAngle') as number;
      const origScale = cardSprite.getData('originalScale') as number;

      if (cardSprite.cardId === this.selectedCardId) {
        cardSprite.setSelected(true);
        cardSprite.setY(baseY - 100);  // Lift selected card
        cardSprite.setAngle(0);
        cardSprite.setScale(origScale * 1.1);  // Slight scale up when selected
        cardSprite.setDepth(400);
      } else {
        cardSprite.setSelected(false);
        cardSprite.setScale(origScale);
        cardSprite.setY(origY);
        cardSprite.setAngle(origAngle);
      }
    });
  }

  private updateAttackerHighlights(): void {
    const uiStore = useUIStore.getState();
    const attackers = uiStore.selectedAttackers;

    this.slots.forEach((slot) => {
      if (slot.cardSprite) {
        if (attackers.includes(slot.cardSprite.cardId)) {
          slot.cardSprite.highlight(0xff6600);
        } else {
          slot.cardSprite.clearHighlight();
        }
      }
    });
  }

  private showMessage(msg: string): void {
    this.messageText.setText(msg);

    if (msg) {
      // Clear message after delay
      this.time.delayedCall(2000, () => {
        if (this.messageText.text === msg) {
          this.messageText.setText('');
        }
      });
    }
  }

  /**
   * 根據卡牌 ID 找到對應的 CardSprite
   */
  private findCardSpriteById(cardId: string): CardSprite | null {
    for (const slot of this.slots.values()) {
      if (slot.cardSprite?.cardId === cardId) {
        return slot.cardSprite;
      }
    }
    return null;
  }

  /**
   * 播放戰鬥動畫
   */
  async playCombatAnimations(combatResults: CombatResult[]): Promise<void> {
    if (!this.combatAnimationManager) return;

    for (const result of combatResults) {
      const attackerSprite = this.findCardSpriteById(result.attackerId);
      const defenderSprite = this.findCardSpriteById(result.defenderId);

      if (!attackerSprite || !defenderSprite) continue;

      // 播放戰鬥音效
      AudioManager.play('attack');

      // 播放戰鬥序列
      await this.combatAnimationManager.playCombatSequence(
        attackerSprite,
        defenderSprite,
        result
      );

      // 播放受傷/破壞音效
      if (result.destroyedCardId) {
        AudioManager.play('destroy');
      } else {
        AudioManager.play('damage');
      }

      // 短暫延遲再進行下一場戰鬥
      await new Promise((resolve) => this.time.delayedCall(300, () => { resolve(undefined); }));
    }
  }

  /**
   * 顯示升級效果
   */
  async showUpgradeEffects(upgradedUnits: { from: string; to: string; unitId: string }[]): Promise<void> {
    if (!this.combatAnimationManager) return;

    for (const upgrade of upgradedUnits) {
      const sprite = this.findCardSpriteById(upgrade.unitId);
      if (!sprite) continue;

      // 顯示升級文字
      this.combatAnimationManager.showEffectTrigger(sprite.x, sprite.y, `升級! ${upgrade.from} → ${upgrade.to}`);

      // 播放升級動畫
      await this.combatAnimationManager.playUpgradeAnimation(sprite);
    }
  }

  /**
   * 顯示效果訊息
   */
  showEffectMessages(messages: string[]): void {
    if (messages.length === 0) return;
    
    // 顯示第一則訊息，其餘用延遲顯示
    messages.forEach((msg, index) => {
      this.time.delayedCall(index * 1500, () => {
        this.showMessage(msg);
      });
    });
  }

  // ============================================================
  // UI Settings Panel
  // ============================================================

  /**
   * Create gear button in the top-right corner
   */
  private createSettingsGearButton(width: number): void {
    // Position: avoid the timer area (timer is at width-20, y=35)
    // Place gear at (width-70, 35) so it doesn't overlap timer
    this.settingsGearButton = this.add.text(width - 70, 35, '⚙️', {
      fontSize: '28px',
    }).setOrigin(0.5).setDepth(2500).setInteractive({ useHandCursor: true });

    this.settingsGearButton.on('pointerdown', () => {
      this.toggleSettingsPanel();
    });
    this.settingsGearButton.on('pointerover', () => {
      this.settingsGearButton?.setScale(1.2);
    });
    this.settingsGearButton.on('pointerout', () => {
      this.settingsGearButton?.setScale(1.0);
    });
  }

  /**
   * Toggle settings panel visibility
   */
  private toggleSettingsPanel(): void {
    if (this.settingsPanelVisible) {
      this.closeSettingsPanel();
    } else {
      this.openSettingsPanel();
    }
  }

  /**
   * Open the settings panel
   */
  private openSettingsPanel(): void {
    this.closeSettingsPanel();
    this.settingsPanelVisible = true;

    const { width } = this.cameras.main;
    const panelW = 380;
    const panelH = 380;
    const panelX = width - panelW / 2 - 20;
    const panelY = 70 + panelH / 2;

    this.settingsPanelContainer = this.add.container(panelX, panelY);
    this.settingsPanelContainer.setDepth(2600);

    // Background
    const bg = this.add.rectangle(0, 0, panelW, panelH, 0x111122, 0.92);
    bg.setStrokeStyle(2, 0x6666aa);
    this.settingsPanelContainer.add(bg);

    // Title
    const title = this.add.text(0, -panelH / 2 + 24, '⚙️ UI 設定', {
      fontSize: '20px',
      color: '#ffd700',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.settingsPanelContainer.add(title);

    // Settings items
    const store = useUISettingsStore.getState();
    const itemStartY = -panelH / 2 + 65;
    const itemGap = 60;

    const items: { label: string; key: keyof UISettings; min: number; max: number; step: number; value: number }[] = [
      { label: '戰場卡牌大小', key: 'battlefieldScale', min: 0.8, max: 1.5, step: 0.1, value: store.battlefieldScale },
      { label: '手牌大小', key: 'handScale', min: 0.8, max: 2.0, step: 0.1, value: store.handScale },
      { label: '文字大小', key: 'fontScale', min: 0.8, max: 1.5, step: 0.1, value: store.fontScale },
      { label: '資訊面板大小', key: 'infoPanelScale', min: 0.8, max: 1.5, step: 0.1, value: store.infoPanelScale },
    ];

    items.forEach((item, index) => {
      this.createSettingsRow(item.label, item.key, item.min, item.max, item.step, item.value, itemStartY + index * itemGap);
    });

    // Reset button
    const resetY = itemStartY + items.length * itemGap + 10;
    const resetBg = this.add.rectangle(0, resetY, 140, 36, 0x3a2a2a);
    resetBg.setStrokeStyle(1.5, 0x884444);
    resetBg.setInteractive({ useHandCursor: true });
    this.settingsPanelContainer.add(resetBg);

    const resetText = this.add.text(0, resetY, '🔄 重置預設', {
      fontSize: '14px',
      color: '#ff8888',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.settingsPanelContainer.add(resetText);

    resetBg.on('pointerover', () => { resetBg.setFillStyle(0x5a3a3a); });
    resetBg.on('pointerout', () => { resetBg.setFillStyle(0x3a2a2a); });
    resetBg.on('pointerdown', () => {
      useUISettingsStore.getState().resetToDefaults();
      // Re-open panel to refresh values
      this.openSettingsPanel();
    });

    // Close button
    const closeBtn = this.add.text(panelW / 2 - 16, -panelH / 2 + 16, '✕', {
      fontSize: '20px',
      color: '#ff6666',
      fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => { this.closeSettingsPanel(); });
    closeBtn.on('pointerover', () => { closeBtn.setColor('#ff9999'); });
    closeBtn.on('pointerout', () => { closeBtn.setColor('#ff6666'); });
    this.settingsPanelContainer.add(closeBtn);

    // Make background block clicks through
    bg.setInteractive();

    // Fade in
    this.settingsPanelContainer.setAlpha(0);
    this.tweens.add({
      targets: this.settingsPanelContainer,
      alpha: 1,
      duration: 150,
      ease: 'Power2',
    });
  }

  /**
   * Create a settings row with label, ◀ ▶ buttons, and value display
   */
  private createSettingsRow(
    label: string,
    key: keyof UISettings,
    min: number,
    max: number,
    step: number,
    currentValue: number,
    y: number
  ): void {
    if (!this.settingsPanelContainer) return;

    // Label
    const labelText = this.add.text(-150, y - 12, label, {
      fontSize: '14px',
      color: '#cccccc',
    }).setOrigin(0, 0.5);
    this.settingsPanelContainer.add(labelText);

    // Value display
    const valueText = this.add.text(40, y + 14, `${(currentValue * 100).toFixed(0)}%`, {
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.5);
    this.settingsPanelContainer.add(valueText);

    // ◀ decrease button
    const decBg = this.add.rectangle(-40, y + 14, 40, 28, 0x2a2a4a);
    decBg.setStrokeStyle(1, 0x4a4a8a);
    decBg.setInteractive({ useHandCursor: true });
    this.settingsPanelContainer.add(decBg);

    const decText = this.add.text(-40, y + 14, '◀', {
      fontSize: '16px',
      color: '#aaaaff',
    }).setOrigin(0.5);
    this.settingsPanelContainer.add(decText);

    // ▶ increase button
    const incBg = this.add.rectangle(120, y + 14, 40, 28, 0x2a2a4a);
    incBg.setStrokeStyle(1, 0x4a4a8a);
    incBg.setInteractive({ useHandCursor: true });
    this.settingsPanelContainer.add(incBg);

    const incText = this.add.text(120, y + 14, '▶', {
      fontSize: '16px',
      color: '#aaaaff',
    }).setOrigin(0.5);
    this.settingsPanelContainer.add(incText);

    // Progress bar background
    const barWidth = 120;
    const barX = 40;
    const barBg = this.add.rectangle(barX, y + 30, barWidth, 6, 0x333355);
    this.settingsPanelContainer.add(barBg);

    // Progress bar fill
    const fillRatio = (currentValue - min) / (max - min);
    const fillWidth = Math.max(2, fillRatio * barWidth);
    const barFill = this.add.rectangle(barX - barWidth / 2 + fillWidth / 2, y + 30, fillWidth, 6, 0x4488ff);
    this.settingsPanelContainer.add(barFill);

    // Button handlers
    const updateValue = (delta: number) => {
      const store = useUISettingsStore.getState();
      const oldVal = store[key];
      const newVal = Math.round((oldVal + delta) * 100) / 100;
      const clamped = Math.max(min, Math.min(max, newVal));

      const setterName = `set${key.charAt(0).toUpperCase()}${key.slice(1)}` as
        'setBattlefieldScale' | 'setHandScale' | 'setFontScale' | 'setInfoPanelScale';
      store[setterName](clamped);

      // Update display
      valueText.setText(`${(clamped * 100).toFixed(0)}%`);
      const newFillRatio = (clamped - min) / (max - min);
      const newFillWidth = Math.max(2, newFillRatio * barWidth);
      barFill.setDisplaySize(newFillWidth, 6);
      barFill.setX(barX - barWidth / 2 + newFillWidth / 2);
    };

    decBg.on('pointerdown', () => { updateValue(-step); });
    decBg.on('pointerover', () => { decBg.setFillStyle(0x3a3a6a); });
    decBg.on('pointerout', () => { decBg.setFillStyle(0x2a2a4a); });

    incBg.on('pointerdown', () => { updateValue(step); });
    incBg.on('pointerover', () => { incBg.setFillStyle(0x3a3a6a); });
    incBg.on('pointerout', () => { incBg.setFillStyle(0x2a2a4a); });
  }

  /**
   * Close the settings panel
   */
  private closeSettingsPanel(): void {
    this.settingsPanelVisible = false;
    if (this.settingsPanelContainer) {
      this.settingsPanelContainer.destroy();
      this.settingsPanelContainer = null;
    }
  }

  /**
   * Subscribe to UI settings changes and trigger re-render
   */
  private subscribeToUISettings(): void {
    this.settingsUnsubscribe = useUISettingsStore.subscribe(
      (state) => state._version,
      (newVersion) => {
        if (newVersion !== this.lastUISettingsVersion) {
          this.lastUISettingsVersion = newVersion;
          this.applyUISettings();
        }
      }
    );
    // Apply initial settings
    this.lastUISettingsVersion = useUISettingsStore.getState()._version;
  }

  /**
   * Apply UI settings by re-rendering affected elements
   */
  private applyUISettings(): void {
    if (!this.gameState) return;

    const settings = useUISettingsStore.getState();

    // Apply infoPanelScale to zone slots and hero portraits
    this.p1DeckSlot.setScale(settings.infoPanelScale);
    this.p1GraveyardSlot.setScale(settings.infoPanelScale);
    this.p1ForestSlot.setScale(settings.infoPanelScale);
    this.p2DeckSlot.setScale(settings.infoPanelScale);
    this.p2GraveyardSlot.setScale(settings.infoPanelScale);
    this.p2ForestSlot.setScale(settings.infoPanelScale);
    this.p1HeroPortrait.setScale(settings.infoPanelScale);
    this.p2HeroPortrait.setScale(settings.infoPanelScale);

    // Apply fontScale to key text elements
    const fs = settings.fontScale;
    this.phaseText.setFontSize(Math.round(16 * fs));
    this.turnText.setFontSize(Math.round(16 * fs));
    this.timerText.setFontSize(Math.round(18 * fs));
    this.messageText.setFontSize(Math.round(20 * fs));
    this.actionHintText.setFontSize(Math.round(16 * fs));

    // Ensure end turn button text scales
    const btnText = this.endTurnButton.getData('text') as Phaser.GameObjects.Text | undefined;
    if (btnText) {
      btnText.setFontSize(Math.round(15 * fs));
    }

    // Re-render battlefield and hands with new scales
    this.updateBattlefield();
    this.updateHands();
  }

  shutdown(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.choiceUnsubscribe) {
      this.choiceUnsubscribe();
    }
    if (this.settingsUnsubscribe) {
      this.settingsUnsubscribe();
      this.settingsUnsubscribe = null;
    }
    if (this.timerUnsubscribe) {
      this.timerUnsubscribe();
      this.timerUnsubscribe = null;
    }
    if (this.aiActionTimeout) {
      clearTimeout(this.aiActionTimeout);
      this.aiActionTimeout = null;
    }
    if (this.autoAdvanceTimeout) {
      clearTimeout(this.autoAdvanceTimeout);
      this.autoAdvanceTimeout = null;
    }
    this.isAutoAdvancing = false;
    this.ai = null;
    this.hideCardPreview();
    this.closeZoneBrowser();
    this.closeSettingsPanel();
    if (this.phaseTransitionContainer) {
      this.phaseTransitionContainer.destroy();
      this.phaseTransitionContainer = null;
    }
    this.clearChoiceHighlights();
  }

  update(): void {
    // Attack arrow is now handled by drag events (setupDragAndDrop).
    // No per-frame arrow drawing needed.
  }
}
